import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Slider,
  Tab,
  Tabs,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  Stack,
} from '@mui/material';
import ComputerIcon from '@mui/icons-material/Computer';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Call';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VolumeDownIcon from '@mui/icons-material/VolumeDown';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment-timezone';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import SearchableSelect from '../../components/SearchableSelect';
import {
  activeSpeakerRoles,
  correctDualChannelDrift,
  displaySpeakerRole,
  dualChannelPause,
  dualChannelPlay,
  dualChannelSeek,
  dualChannelSetPlaybackRate,
  findActiveSegments,
  isAgentLikeRole,
  isCustomerLikeRole,
  isSystemLikeRole,
  normalizeTranscriptSegments,
  playbackSpeakerStatus,
} from '../../utils/callQaPlaybackSync';
import {
  highlightTranscriptByFindings,
  highestHighlightSeverity,
  TRANSCRIPT_SEVERITY_STYLE,
  TranscriptHighlightSpan,
} from '../../utils/callQaTranscriptHighlights';

function formatDuration(seconds?: number | null): string {
  if (seconds == null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

/** True when measured audio is much shorter than the listed call/session window. */
function audioMuchShorterThanSession(sessionSec?: number | null, audioSec?: number | null): boolean {
  const session = Number(sessionSec || 0);
  const audio = Number(audioSec || 0);
  return session >= 60 && audio > 0 && audio < session * 0.5;
}

function formatCallDurationLabel(sessionSec?: number | null, audioSec?: number | null): string {
  const session = sessionSec != null ? Number(sessionSec) : null;
  const audio = audioSec != null ? Number(audioSec) : null;
  if (audioMuchShorterThanSession(session, audio)) {
    return `${formatDuration(audio)} audio · session ${formatDuration(session)}`;
  }
  if (session != null && session > 0) return formatDuration(session);
  if (audio != null && audio > 0) return formatDuration(audio);
  return '—';
}

function formatTimestamp(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** Player clock, matching the transport readout (2:17 / 3:33). */
function formatMediaClock(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const clock = `${m}:${String(s).padStart(2, '0')}`;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : clock;
}

function speakerColor(role: string): 'primary' | 'secondary' | 'default' | 'warning' {
  if (isAgentLikeRole(role)) return 'primary';
  if (isCustomerLikeRole(role)) return 'secondary';
  if (isSystemLikeRole(role)) return 'warning';
  return 'default';
}

function speakerLabel(role: string): string {
  return displaySpeakerRole(role);
}

const CLASSIFICATION_LABELS: Record<string, string> = {
  RIGHT_PARTY: 'Right party',
  THIRD_PARTY: 'Third party',
  UNCLEAR: 'Unclear',
  UNKNOWN: 'Unknown',
};

function classificationChip(raw?: string | null): { label: string; color: 'default' | 'success' | 'warning' | 'info' } {
  const key = String(raw || 'UNKNOWN').toUpperCase();
  const label = CLASSIFICATION_LABELS[key] || 'Unknown';
  // UNKNOWN must remain visibly neutral — never dressed up as a determined result.
  const color = key === 'RIGHT_PARTY' ? 'success'
    : key === 'THIRD_PARTY' ? 'warning'
      : key === 'UNCLEAR' ? 'info'
        : 'default';
  return { label, color };
}

function disclosureChip(raw?: string | null): { label: string; tone: 'missing' | 'present' | 'neutral' } | null {
  const key = String(raw || '').toUpperCase();
  if (!key || key === 'UNKNOWN' || key === 'NOT_APPLICABLE' || key === 'N/A') return null;
  if (key === 'MISSING' || key === 'NO_DISCLOSURE' || key === 'ABSENT') {
    return { label: 'No disclosure', tone: 'missing' };
  }
  if (key === 'PRESENT' || key === 'DISCLOSED' || key === 'GIVEN') {
    return { label: 'Disclosure given', tone: 'present' };
  }
  return { label: key.replace(/_/g, ' ').toLowerCase(), tone: 'neutral' };
}

/** Deterministic rubric dump like "Classification: unclear; …; 0 finding(s) (0 serious, 0 warning)." */
function isSparseCountNarrative(text?: string | null): boolean {
  const t = String(text || '').trim();
  if (!t) return true;
  return /^Classification:\s*.+;\s*.+;\s*\d+\s*finding\(s\)/i.test(t);
}

function formatFindingsProse(counts: {
  warning: number;
  serious: number;
  critical: number;
  notes: number;
}): string {
  const parts: string[] = [];
  if (counts.critical > 0) parts.push(`${counts.critical} critical`);
  if (counts.serious > 0) parts.push(`${counts.serious} serious`);
  if (counts.warning > 0) parts.push(`${counts.warning} warning${counts.warning === 1 ? '' : 's'}`);
  if (counts.notes > 0) parts.push(`${counts.notes} note${counts.notes === 1 ? '' : 's'}`);
  const total = counts.critical + counts.serious + counts.warning + counts.notes;
  if (total === 0) return 'No findings were flagged for review.';
  return `There ${total === 1 ? 'is' : 'are'} ${total} finding${total === 1 ? '' : 's'} (${parts.join(', ')}).`;
}

/**
 * User-facing SUMMARY narrative: prefer a rich stored summary when present; otherwise
 * derive readable prose from classification + disclosure + live finding counts.
 * Never trust sparse deterministic count lines when live notes disagree.
 */
function buildCallSummaryNarrative(opts: {
  storedNarrative?: string | null;
  classification: string;
  disclosureStatus?: string | null;
  analysisComplete: boolean;
  counts: { warning: number; serious: number; critical: number; notes: number };
  themes?: string[];
}): string {
  const classKey = String(opts.classification || 'UNKNOWN').toUpperCase();
  const classLabel = (CLASSIFICATION_LABELS[classKey] || classKey.replace(/_/g, ' ')).toLowerCase();
  const disc = disclosureChip(opts.disclosureStatus);
  const discSentence = disc
    ? (disc.tone === 'present'
      ? 'Required disclosure was detected.'
      : disc.tone === 'missing'
        ? 'Required disclosure was not detected.'
        : `Disclosure: ${disc.label}.`)
    : '';
  const findingsProse = formatFindingsProse(opts.counts);
  const themes = (opts.themes || []).map((t) => String(t || '').trim()).filter(Boolean).slice(0, 4);
  const themesSentence = themes.length ? ` Key themes: ${themes.join('; ')}.` : '';

  if (!opts.analysisComplete) {
    return 'Automated analysis has not completed for this call yet.';
  }

  const stored = String(opts.storedNarrative || '').trim();
  if (stored && !isSparseCountNarrative(stored)) {
    const lead = /[.!?]$/.test(stored) ? stored : `${stored}.`;
    return `${lead} ${findingsProse}${themesSentence}`.replace(/\s+/g, ' ').trim();
  }

  return `This call was classified as ${classLabel}. ${discSentence} ${findingsProse}${themesSentence}`
    .replace(/\s+/g, ' ')
    .trim();
}

function HighlightedTranscriptText({ text, spans }: { text: string; spans: TranscriptHighlightSpan[] }) {
  if (!spans.length) return <>{text}</>;
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  spans.forEach((span, index) => {
    if (span.start > cursor) parts.push(text.slice(cursor, span.start));
    const style = TRANSCRIPT_SEVERITY_STYLE[span.severity];
    parts.push(
      <Box
        key={`${span.start}-${span.end}-${index}`}
        component="mark"
        data-testid="transcript-finding-mark"
        data-severity={span.severity}
        title={span.title || undefined}
        style={{
          backgroundColor: style.background,
          color: 'inherit',
          borderBottom: `2px solid ${style.swatch}`,
          borderRadius: 2,
          padding: '0 2px',
        }}
      >
        {text.slice(span.start, span.end)}
      </Box>,
    );
    cursor = span.end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <>{parts}</>;
}

/** Identify the communication system / app a desktop call was placed on. */
function friendlyCommSystem(call: any): string {
  const fromNotes = /softphone=([^\s;,]+)/i.exec(String(call?.notes || ''));
  let raw = fromNotes ? fromNotes[1] : '';
  if (!raw && call?.provider && !['DESKTOP', ''].includes(String(call.provider).toUpperCase())) {
    raw = String(call.provider);
  }
  if (!raw) {
    return String(call?.recording_source || '').toUpperCase() === 'DESKTOP_CAPTURE' ? 'Desktop capture' : '';
  }
  const key = raw.toLowerCase().replace(/\.exe$/, '');
  const map: Record<string, string> = {
    teams: 'Microsoft Teams', 'ms-teams': 'Microsoft Teams', msteams: 'Microsoft Teams',
    msedge: 'Microsoft Edge', edge: 'Microsoft Edge', chrome: 'Google Chrome', firefox: 'Firefox',
    zoom: 'Zoom', ringcentral: 'RingCentral', genesys: 'Genesys Cloud', webex: 'Webex',
    goto: 'GoTo', gotoconnect: 'GoTo', dialpad: 'Dialpad', five9: 'Five9', '8x8': '8x8',
    softphone: 'Softphone', skype: 'Skype',
  };
  if (map[key]) return map[key];
  return key.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function callInitials(name?: string | null): string {
  const base = String(name || 'Call').trim();
  const parts = base.split(/\s+/).filter(Boolean);
  const letters = (parts.length >= 2 ? [parts[0][0], parts[1][0]] : [base[0] || 'C']);
  return letters.join('').toUpperCase().slice(0, 2);
}

/** Compact circular QA verdict gauge. */
type ConductSev = 'warning' | 'serious' | 'critical';
const CONDUCT_SEV_RANK: Record<string, number> = { warning: 1, serious: 2, critical: 3 };

interface CallProcessingCtx {
  analysisComplete: boolean;
  transcriptionStatus: string;
  qaStatus: string;
  providerBlocked: boolean;
}

/**
 * The single conduct indicator for a call. Derived ONLY from independent Agent Conduct
 * incidents — never from a scorecard result. A low QA score alone yields "No conduct findings".
 * When there are no incidents, the label distinguishes done / in-progress / unavailable / failed
 * so the analyst knows whether to wait, act, or that conduct simply cannot run here.
 */
function conductCallStatus(
  incidents: any[],
  ctx: CallProcessingCtx,
): { label: string; tone: 'none' | 'warning' | 'serious' | 'critical' | 'pending' | 'confirmed' | 'unavailable'; severity: ConductSev | null } {
  const active = (incidents || []).filter((i) => !i.superseded);
  if (active.length === 0) {
    const t = String(ctx.transcriptionStatus || '').toUpperCase();
    const qa = String(ctx.qaStatus || '').toUpperCase();
    if (ctx.providerBlocked || t === 'BLOCKED') return { label: 'Conduct analysis unavailable (AI not configured)', tone: 'unavailable', severity: null };
    if (t === 'FAILED') return { label: 'Conduct analysis unavailable (no transcript)', tone: 'unavailable', severity: null };
    if (t === 'PENDING' || t === 'PROCESSING') return { label: 'Transcription in progress', tone: 'pending', severity: null };
    if (qa === 'PENDING' || qa === 'PROCESSING') return { label: 'Analysis in progress', tone: 'pending', severity: null };
    if (ctx.analysisComplete) return { label: 'No conduct findings', tone: 'none', severity: null };
    return { label: 'Awaiting analysis', tone: 'pending', severity: null };
  }
  const confirmed = active.some((i) => String(i.review_status).toUpperCase() === 'CONFIRMED');
  let top: ConductSev = 'warning';
  for (const i of active) {
    const s = String(i.severity || '').toLowerCase();
    if ((CONDUCT_SEV_RANK[s] || 0) > CONDUCT_SEV_RANK[top]) top = s as ConductSev;
  }
  const sevLabel = top.charAt(0).toUpperCase() + top.slice(1);
  if (confirmed) return { label: `Confirmed incident · ${sevLabel}`, tone: 'confirmed', severity: top };
  return { label: `${sevLabel} — review required`, tone: top, severity: top };
}

/**
 * The QA Scorecard tile value — distinguishes an actual scorecard RESULT (Pass/Review/Fail)
 * from a processing state (in progress / failed / blocked / not scored). A processing failure
 * must never read like a scorecard "Fail".
 */
function scorecardTileStatus(
  score: any,
  ctx: CallProcessingCtx,
): { value: string; color: 'default' | 'success' | 'warning' | 'error'; score?: number | null; word?: string } {
  if (score && score.final_overall_score != null) {
    const st = String(score.final_overall_status || 'REVIEW').toUpperCase();
    const word = st === 'PASS' ? 'Pass' : st === 'FAIL' ? 'Fail' : 'Review';
    // Red is reserved for conduct; a scorecard Fail uses amber.
    return {
      value: `${score.final_overall_score}% · ${word}`,
      color: st === 'PASS' ? 'success' : st === 'FAIL' ? 'warning' : 'default',
      score: Number(score.final_overall_score),
      word,
    };
  }
  const t = String(ctx.transcriptionStatus || '').toUpperCase();
  const qa = String(ctx.qaStatus || '').toUpperCase();
  if (ctx.providerBlocked || t === 'BLOCKED' || qa === 'BLOCKED') return { value: 'Not scored · AI unavailable', color: 'default' };
  if (t === 'FAILED') return { value: 'Not scored · transcription failed', color: 'default' };
  if (qa === 'FAILED') return { value: 'Not scored · scoring failed', color: 'default' };
  if (t === 'PENDING' || t === 'PROCESSING') return { value: 'Transcription in progress', color: 'default' };
  if (qa === 'PENDING' || qa === 'PROCESSING') return { value: 'Scoring in progress', color: 'default' };
  if (ctx.analysisComplete) return { value: 'Not scored', color: 'default' };
  return { value: 'Awaiting analysis', color: 'default' };
}

/** MUI palette color for a conduct tone. Strong red reserved for serious/critical conduct only. */
function conductToneColor(tone: string): 'default' | 'success' | 'warning' | 'error' | 'info' {
  if (tone === 'critical' || tone === 'serious' || tone === 'confirmed') return 'error';
  if (tone === 'warning') return 'warning';
  if (tone === 'none') return 'success';
  return 'default';
}

/** Compact, friendly review-status label. */
function reviewStatusLabel(raw?: string | null): string {
  const s = String(raw || 'NONE').toUpperCase();
  const map: Record<string, string> = {
    NONE: 'Not reviewed',
    PENDING_REVIEW: 'Pending review',
    IN_REVIEW: 'In review',
    ACCEPTED: 'Confirmed',
    CONFIRMED: 'Confirmed',
    OVERRIDDEN: 'Overridden',
    DISMISSED: 'Dismissed',
    COACHING_REQUIRED: 'Coaching required',
    RESOLVED: 'Resolved',
  };
  return map[s] || s.replace(/_/g, ' ').toLowerCase();
}

/** A compact measurement tile for the call header (Conduct / Scorecard / Review). */
const MeasurementTile: React.FC<{
  heading: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  color?: 'default' | 'success' | 'warning' | 'error' | 'info';
  onClick?: () => void;
  testid?: string;
}> = ({ heading, value, sub, color = 'default', onClick, testid }) => {
  const border = color === 'error' ? 'error.main'
    : color === 'warning' ? 'warning.main'
      : color === 'success' ? 'success.light'
        : 'divider';
  return (
    <Paper
      variant="outlined"
      data-testid={testid}
      onClick={onClick}
      sx={{
        p: 1.75,
        borderRadius: 2,
        borderLeft: 4,
        borderLeftColor: border,
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { bgcolor: 'action.hover' } : undefined,
      }}
    >
      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
        {heading}
      </Typography>
      <Typography component="div" sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.3, mt: 0.4 }}>{value}</Typography>
      {sub != null && <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mt: 0.25 }} display="block">{sub}</Typography>}
    </Paper>
  );
};

/** Arc colors for the QA score ring, keyed to the scorecard tile's status color. */
const SCORE_RING_COLORS: Record<string, string> = {
  success: '#2e7d32', // Pass
  warning: '#b45309',  // Fail (amber — red is reserved for conduct)
  error: '#c62828',
  default: '#546e7a',  // Review / neutral
};

/**
 * Circular (donut) representation of a QA scorecard percentage, with the number and the
 * result word (Pass / Review / Fail) in the center. Rendered inside the QA Scorecard tile.
 */
const ScoreRing: React.FC<{
  value: number;
  word?: string;
  color?: 'default' | 'success' | 'warning' | 'error';
  size?: number;
}> = ({ value, word, color = 'default', size = 66 }) => {
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  const stroke = 6;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  const arc = SCORE_RING_COLORS[color] || SCORE_RING_COLORS.default;
  return (
    <Box
      sx={{ position: 'relative', width: size, height: size, mt: 0.5 }}
      data-testid="scorecard-ring"
      role="img"
      aria-label={`QA score ${pct}%${word ? ` · ${word}` : ''}`}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e3e8ef" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={arc}
          strokeWidth={stroke}
          strokeDasharray={`${dash} ${circ - dash}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Typography component="span" sx={{ fontSize: size * 0.3, fontWeight: 800, lineHeight: 1, color: arc, fontVariantNumeric: 'tabular-nums' }}>
          {pct}%
        </Typography>
        {word && (
          <Typography component="span" sx={{ fontSize: size * 0.15, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3, lineHeight: 1.1 }}>
            {word}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

/** Small number-over-label stat tile with a colored accent bar (distinct from flat pills). */
const StatTile: React.FC<{ n: number | string; label: string; accent?: 'amber' | 'red' | 'green'; testid?: string }> = ({ n, label, accent, testid }) => {
  const barColor = accent === 'amber' ? '#b45309' : accent === 'red' ? '#c62828' : accent === 'green' ? '#2e7d32' : '#d2d9e6';
  const numColor = accent === 'amber' ? '#b45309' : accent === 'red' ? '#c62828' : 'text.primary';
  return (
    <Paper variant="outlined" data-testid={testid} sx={{ position: 'relative', minWidth: 90, px: 1.25, py: 1, pl: 1.75, borderRadius: 2 }}>
      <Box sx={{ position: 'absolute', left: 0, top: 8, bottom: 8, width: 3, borderRadius: 2, bgcolor: barColor }} />
      <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.05, color: numColor, fontVariantNumeric: 'tabular-nums' }}>{n}</Typography>
      <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{label}</Typography>
    </Paper>
  );
};

const ImportDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  employees: any[];
  campaigns: any[];
}> = ({ open, onClose, employees, campaigns }) => {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [startedAt, setStartedAt] = useState(moment().format('YYYY-MM-DDTHH:mm'));
  const [direction, setDirection] = useState('UNKNOWN');
  const [employeeId, setEmployeeId] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [contractNumber, setContractNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [provider, setProvider] = useState('');
  const [providerCallId, setProviderCallId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Recording file is required.');
      if (!startedAt) throw new Error('Date/time is required.');
      const form = new FormData();
      form.append('recording', file);
      form.append('started_at', new Date(startedAt).toISOString());
      form.append('direction', direction);
      if (employeeId) form.append('employee_id', employeeId);
      if (campaignId) form.append('campaign_id', campaignId);
      if (customerName) form.append('customer_name', customerName);
      if (customerPhone) form.append('customer_phone', customerPhone);
      if (accountNumber) form.append('account_number', accountNumber);
      if (contractNumber) form.append('contract_number', contractNumber);
      if (notes) form.append('notes', notes);
      if (provider) form.append('provider', provider);
      if (providerCallId) form.append('provider_call_id', providerCallId);
      return apiClient.callQa.importRecording(form);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callQaCalls'] });
      queryClient.invalidateQueries({ queryKey: ['callQaOverview'] });
      onClose();
    },
    onError: (err: any) => {
      setError(err?.response?.data?.message || err?.message || 'Import failed.');
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import Recording</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Supported: wav, mp3, m4a, ogg, opus. Source: MANUAL_UPLOAD.
        </Typography>
        <Button variant="outlined" component="label" sx={{ mb: 2 }}>
          {file ? file.name : 'Choose recording file *'}
          <input
            hidden
            type="file"
            accept=".wav,.mp3,.m4a,.ogg,.opus,audio/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </Button>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Date/time *"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={startedAt}
              onChange={(e) => setStartedAt(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Direction</InputLabel>
              <Select label="Direction" value={direction} onChange={(e) => setDirection(e.target.value)}>
                <MenuItem value="UNKNOWN">Unknown</MenuItem>
                <MenuItem value="INBOUND">Inbound</MenuItem>
                <MenuItem value="OUTBOUND">Outbound</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Agent</InputLabel>
              <Select label="Agent" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
                <MenuItem value="">Unlinked</MenuItem>
                {employees.map((e) => (
                  <MenuItem key={e.id} value={e.id}>{e.firstName} {e.lastName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Campaign</InputLabel>
              <Select label="Campaign" value={campaignId} onChange={(e) => setCampaignId(e.target.value)}>
                <MenuItem value="">None</MenuItem>
                {campaigns.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Customer Name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Customer Phone" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Account Number" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Contract Number" value={contractNumber} onChange={(e) => setContractNumber(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Provider (optional)" value={provider} onChange={(e) => setProvider(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Provider Call ID" value={providerCallId} onChange={(e) => setProviderCallId(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" multiline minRows={2} label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={mutation.isPending} onClick={() => { setError(null); mutation.mutate(); }}>
          {mutation.isPending ? 'Importing…' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const CallQaCallDetailPage: React.FC<{ callIdProp?: string; embedded?: boolean }> = ({ callIdProp, embedded }) => {
  const params = useParams();
  const callId = callIdProp ?? params.callId;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canListen = permissions.includes('call_qa.recordings.listen');
  const canDownload = permissions.includes('call_qa.recordings.download');
  const canUpdate = permissions.includes('call_qa.calls.update');
  const canReadTranscript = permissions.includes('call_qa.transcripts.read');
  const canReadScore = permissions.includes('call_qa.scoring.read');
  const canRunScore = permissions.includes('call_qa.scoring.run');
  const canOverrideScore = permissions.includes('call_qa.scoring.override');
  const isSuperUser = user?.role === 'super_user';
  const canManageInvestigations = permissions.includes('call_qa.investigations.manage');
  const canManageCoaching = permissions.includes('call_qa.coaching.manage');
  const queryClient = useQueryClient();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const contactAudioRef = useRef<HTMLAudioElement | null>(null);
  const transcriptScrollRef = useRef<HTMLDivElement | null>(null);
  const segmentElRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const suppressAutoScrollRef = useRef(false);
  const lastTickRef = useRef(0);
  const [currentMs, setCurrentMs] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [volume, setVolume] = useState(1);
  const [masterMuted, setMasterMuted] = useState(false);
  const [scrubMs, setScrubMs] = useState<number | null>(null);
  const [transportMenuAnchor, setTransportMenuAnchor] = useState<HTMLElement | null>(null);
  const [followPlayback, setFollowPlayback] = useState(true);
  const [collectorMuted, setCollectorMuted] = useState(false);
  const [contactMuted, setContactMuted] = useState(false);
  const [withinQuery, setWithinQuery] = useState('');
  const [withinMatches, setWithinMatches] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'conduct' | 'scorecard' | 'transcript' | 'details'>('conduct');
  const initialSeek = Number(searchParams.get('seek_ms') || 0);

  const { data: usersData } = useQuery({
    queryKey: ['usersForCallQa'],
    queryFn: () => apiClient.users.getAll(),
    enabled: canUpdate,
  });
  const { data: campaignsData } = useQuery({
    queryKey: ['campaignsForCallQa'],
    queryFn: () => apiClient.campaigns.getAll(),
    enabled: canUpdate,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['callQaCall', callId],
    queryFn: () => apiClient.callQa.getCall(String(callId)),
    enabled: Boolean(callId),
    refetchInterval: (query) => {
      if (process.env.NODE_ENV === 'test') return false;
      const status = query.state.data?.data?.data?.transcription_status;
      const qa = query.state.data?.data?.data?.qa_status;
      if (status === 'PENDING' || status === 'PROCESSING') return 2000;
      if (qa === 'PENDING' || qa === 'PROCESSING') return 2000;
      return false;
    },
  });

  const {
    data: transcriptData,
    isLoading: transcriptLoading,
    isFetching: transcriptFetching,
  } = useQuery({
    queryKey: ['callQaTranscript', callId],
    queryFn: () => apiClient.callQa.getTranscript(String(callId)),
    enabled: Boolean(callId) && canReadTranscript,
    refetchInterval: (query) => {
      if (process.env.NODE_ENV === 'test') return false;
      const status = query.state.data?.data?.data?.transcription_status;
      return status === 'PENDING' || status === 'PROCESSING' ? 2000 : false;
    },
  });

  const {
    data: scoreData,
    isLoading: scoreLoading,
  } = useQuery({
    queryKey: ['callQaScore', callId],
    queryFn: () => apiClient.callQa.getCallScore(String(callId)),
    enabled: Boolean(callId) && canReadScore,
    refetchInterval: (query) => {
      if (process.env.NODE_ENV === 'test') return false;
      const qa = query.state.data?.data?.data?.qa_status;
      return qa === 'PENDING' || qa === 'PROCESSING' ? 2000 : false;
    },
  });

  const { data: activityNotesData, refetch: refetchNotes } = useQuery({
    queryKey: ['callQaActivityNotes', callId],
    queryFn: () => apiClient.callQa.getActivityNotes(String(callId)),
    enabled: Boolean(callId),
  });

  // Independent Agent Conduct incidents for this call (separate from the scorecard).
  const { data: conductData } = useQuery({
    queryKey: ['callQaConduct', callId],
    queryFn: () => apiClient.callQa.getCallConduct(String(callId)),
    enabled: Boolean(callId),
  });
  const updateConductIncident = useMutation({
    mutationFn: (payload: { id: string; review_status: string }) =>
      apiClient.callQa.updateConductIncident(payload.id, { review_status: payload.review_status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callQaConduct', callId] });
      queryClient.invalidateQueries({ queryKey: ['callQaConductIncidents'] });
      queryClient.invalidateQueries({ queryKey: ['callQaCalls'] });
    },
  });

  const [noteTitle, setNoteTitle] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const createNote = useMutation({
    mutationFn: () => apiClient.callQa.createActivityNote(String(callId), {
      title: noteTitle,
      description: noteBody,
      category: 'human_note',
      severity: 'note',
      start_ms: currentMs || null,
    }),
    onSuccess: () => {
      setNoteTitle('');
      setNoteBody('');
      refetchNotes();
      queryClient.invalidateQueries({ queryKey: ['callQaCalls'] });
    },
  });
  const reviewNote = useMutation({
    mutationFn: (payload: { id: string; status: string }) =>
      apiClient.callQa.reviewActivityNote(payload.id, { status: payload.status }),
    onSuccess: () => {
      refetchNotes();
      queryClient.invalidateQueries({ queryKey: ['callQaCalls'] });
    },
  });
  const updateDisposition = useMutation({
    mutationFn: (disposition: string) =>
      apiClient.callQa.updateCall(String(callId), { disposition }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['callQaCall', callId] }),
  });

  const requestScoring = useMutation({
    mutationFn: (payload: { force?: boolean; mode?: string }) =>
      apiClient.callQa.requestScoring(String(callId), payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callQaScore', callId] });
      queryClient.invalidateQueries({ queryKey: ['callQaCall', callId] });
    },
  });
  // Super-user re-analyze: re-run deterministic rubric + AI scoring on the existing transcript.
  const reanalyze = useMutation({
    mutationFn: () => apiClient.callQa.reanalyzeCall(String(callId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callQaScore', callId] });
      queryClient.invalidateQueries({ queryKey: ['callQaCall', callId] });
      queryClient.invalidateQueries({ queryKey: ['callQaTranscript', callId] });
    },
  });
  const acceptScore = useMutation({
    mutationFn: () => apiClient.callQa.acceptScore(String(callId)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['callQaScore', callId] }),
  });
  const overrideScore = useMutation({
    mutationFn: (criterionId: string) => apiClient.callQa.overrideScore(String(callId), {
      results: [{
        criterion_id: criterionId,
        result: 'PASS',
        reason: 'Manual override to PASS by reviewer.',
        notes: 'Human override',
      }],
      review_notes: 'Criterion overridden in Call Review',
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['callQaScore', callId] }),
  });
  const createInvestigation = useMutation({
    mutationFn: () => {
      const c = data?.data?.data;
      return apiClient.callQa.createInvestigation({
        call_id: callId,
        complaint_description: `Customer complaint investigation for call ${callId}`,
        customer_name: c?.customer_name || null,
        customer_phone: c?.customer_phone || null,
        account_number: c?.account_number || null,
        campaign_id: c?.campaign_id || null,
        employee_id: c?.employee_id || null,
        source: 'CALL_REVIEW',
      });
    },
    onSuccess: (res) => {
      const id = res.data?.data?.id;
      if (id) navigate(`/call-qa/investigations/${id}`);
    },
  });
  const createCoaching = useMutation({
    mutationFn: () => {
      const c = data?.data?.data;
      const criteria = scoreData?.data?.data?.score?.criteria || [];
      return apiClient.callQa.createCoaching({
        employee_id: c?.employee_id,
        campaign_id: c?.campaign_id,
        call_ids: [String(callId)],
        topic: 'Call QA coaching follow-up',
        qa_categories: criteria.slice(0, 3).map((x: any) => x.section).filter(Boolean),
        description: `Created from Call Review for ${c?.customer_name || callId}`,
      });
    },
    onSuccess: (res) => {
      const id = res.data?.data?.id;
      if (id) navigate(`/call-qa/coaching/${id}`);
    },
  });
  const linkEmp = useMutation({
    mutationFn: (employee_id: string | null) => apiClient.callQa.linkEmployee(String(callId), { employee_id }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['callQaCall', callId] }),
  });
  const linkCamp = useMutation({
    mutationFn: (campaign_id: string | null) => apiClient.callQa.linkCampaign(String(callId), { campaign_id }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['callQaCall', callId] }),
  });
  const requestTranscription = useMutation({
    mutationFn: (force: boolean) => apiClient.callQa.requestTranscription(String(callId), { force, retry: force }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callQaCall', callId] });
      queryClient.invalidateQueries({ queryKey: ['callQaTranscript', callId] });
    },
  });
  const correctSpeaker = useMutation({
    mutationFn: (payload: { segmentId: string; speaker_role: string }) =>
      apiClient.callQa.correctSpeakerRole(payload.segmentId, { speaker_role: payload.speaker_role }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['callQaTranscript', callId] }),
  });
  const searchWithin = useMutation({
    mutationFn: (q: string) => apiClient.callQa.searchWithinCall(String(callId), { q }),
    onSuccess: (res) => setWithinMatches(res.data?.data?.matches || []),
  });

  // Only use call payload when it matches the selected id — prevents stale React Query
  // data from a prior selection driving the player (wrong audio / wrong duration).
  const callRaw = data?.data?.data;
  const call = callRaw && String(callRaw.id) === String(callId) ? callRaw : undefined;
  const transcriptPayload = transcriptData?.data?.data;
  const segments = useMemo(
    () => normalizeTranscriptSegments(transcriptPayload?.segments || []),
    [transcriptPayload?.segments],
  );
  // Ignite-style transcript meta: turn count + per-side talk time.
  const transcriptMeta = useMemo(() => {
    let collectorMs = 0;
    let contactMs = 0;
    for (const s of segments as any[]) {
      const d = Math.max(0, Number(s.end_ms || 0) - Number(s.start_ms || 0));
      if (isAgentLikeRole(s.speaker_role)) collectorMs += d;
      else if (isCustomerLikeRole(s.speaker_role)) contactMs += d;
    }
    return { turns: segments.length, collectorS: Math.round(collectorMs / 1000), contactS: Math.round(contactMs / 1000) };
  }, [segments]);
  const transcriptProvider = transcriptPayload?.transcript?.provider
    || transcriptPayload?.transcript?.source
    || (transcriptPayload?.segments?.length ? 'openai' : null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  /** Client-measured HTMLAudioElement duration (seconds) — fallback when API audio_duration is stale. */
  const [playbackAudioSeconds, setPlaybackAudioSeconds] = useState<number | null>(null);
  const [contactUrl, setContactUrl] = useState<string | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);
  const employees = useMemo(() => {
    const raw = usersData?.data?.data ?? usersData?.data;
    return Array.isArray(raw) ? raw : [];
  }, [usersData]);
  const campaigns = useMemo(() => {
    const raw = campaignsData?.data?.data ?? campaignsData?.data;
    return Array.isArray(raw) ? raw : [];
  }, [campaignsData]);

  // Capture mode is authoritative (new multi-source model); fall back to the legacy
  // audio_channel_mode only when capture_mode is absent.
  const captureMode = String(call?.capture_mode || call?.audio_channel_mode || 'MIXED').toUpperCase();
  const recordingsList: any[] = useMemo(
    () => (Array.isArray(call?.recordings) ? call.recordings : []),
    [call?.recordings],
  );
  const collectorRecording = useMemo(
    () => recordingsList.find((r) => String(r.source_type).toUpperCase() === 'COLLECTOR' && r.is_streamable),
    [recordingsList],
  );
  const remoteRecording = useMemo(
    () => recordingsList.find((r) => {
      const t = String(r.source_type).toUpperCase();
      return (t === 'REMOTE_SYSTEM' || t === 'SYSTEM' || t === 'CONTACT') && r.is_streamable;
    }),
    [recordingsList],
  );
  const mixedRecording = useMemo(
    () => {
      const mixed = recordingsList.filter((r) => String(r.source_type || 'MIXED').toUpperCase() === 'MIXED' && r.is_streamable);
      if (mixed.length === 0) return null;
      const targetMs = Number(call?.duration_seconds || 0) * 1000;
      return [...mixed].sort((a, b) => {
        const da = Math.abs(Number(a.duration_ms || 0) - targetMs);
        const db = Math.abs(Number(b.duration_ms || 0) - targetMs);
        if (targetMs > 0 && da !== db) return da - db;
        return Number(b.duration_ms || 0) - Number(a.duration_ms || 0);
      })[0];
    },
    [recordingsList, call?.duration_seconds],
  );
  // Prefer a MIXED file that matches listed duration when separated legs are much shorter
  // (common when a leftover short channel would make the native player show ~1–2 min).
  const separatedLegsTooShort = useMemo(() => {
    if (!collectorRecording || !remoteRecording) return false;
    const listed = Number(call?.duration_seconds || 0);
    if (listed < 60) return false;
    const longestLeg = Math.max(
      Number(collectorRecording.duration_ms || 0),
      Number(remoteRecording.duration_ms || 0),
    ) / 1000;
    return longestLeg > 0 && longestLeg < listed * 0.5;
  }, [collectorRecording, remoteRecording, call?.duration_seconds]);
  // Only offer two-track controls when there are genuinely two separate media files
  // whose lengths are consistent with the listed call duration.
  const hasDualChannelAudio = captureMode === 'SEPARATED'
    && Boolean(collectorRecording)
    && Boolean(remoteRecording)
    && !separatedLegsTooShort;
  const contactOffsetSec = useMemo(() => {
    if (!hasDualChannelAudio) return 0;
    const c = Number(collectorRecording?.start_offset_ms) || 0;
    const r = Number(remoteRecording?.start_offset_ms) || 0;
    return Math.max(0, (r - c) / 1000);
  }, [hasDualChannelAudio, collectorRecording, remoteRecording]);
  const captureModeLabel: Record<string, string> = {
    SEPARATED: 'Two channels: Collector + Remote',
    MIXED: 'Mixed Recording',
    COLLECTOR_ONLY: 'Collector microphone only',
    SYSTEM_ONLY: 'Remote/System audio only',
    CONTACT_ONLY: 'Contact audio only',
    UNAVAILABLE: 'No audio captured',
  };

  // Two-track playback (Collector + Remote) is summed by the browser into both speakers. Because the
  // collector mic also picks up the remote party (bleed, ~25 ms late) and the Remote track has that
  // same voice, playing both centered makes the remote voice comb-filter into a "robotic" sound.
  // Route each track to a different ear (Collector → left, Contact → right) via Web Audio so the
  // duplicated voice no longer sums destructively. Falls back to default playback where Web Audio or
  // StereoPanner is unavailable. The two <audio> elements are always mounted so the taps stay valid.
  const panGraphRef = useRef<{
    ctx: AudioContext;
    collectorPan: StereoPannerNode;
    contactPan: StereoPannerNode;
  } | null>(null);

  const ensureStereoSplit = useCallback(() => {
    const Ctor: typeof AudioContext | undefined =
      (window as any).AudioContext || (window as any).webkitAudioContext;
    const collectorEl = audioRef.current;
    const contactEl = contactAudioRef.current;
    if (!Ctor || !collectorEl || !contactEl) return;
    try {
      let g = panGraphRef.current;
      if (!g) {
        const ctx = new Ctor();
        if (typeof ctx.createStereoPanner !== 'function') {
          // No StereoPanner (very old browser) — leave default summed playback.
          try { void ctx.close(); } catch { /* ignore */ }
          return;
        }
        const collectorSrc = ctx.createMediaElementSource(collectorEl);
        const contactSrc = ctx.createMediaElementSource(contactEl);
        const collectorPan = ctx.createStereoPanner();
        const contactPan = ctx.createStereoPanner();
        contactPan.pan.value = 1; // Contact (remote/customer) → right
        collectorSrc.connect(collectorPan).connect(ctx.destination);
        contactSrc.connect(contactPan).connect(ctx.destination);
        g = { ctx, collectorPan, contactPan };
        panGraphRef.current = g;
      }
      // Collector → left only while two real tracks play; centered for single-track calls.
      g.collectorPan.pan.value = hasDualChannelAudio ? -1 : 0;
      if (g.ctx.state === 'suspended') void g.ctx.resume();
    } catch {
      // Web Audio unavailable or element already tapped elsewhere — keep default playback.
    }
  }, [hasDualChannelAudio]);

  // Keep the pan centered/split in sync when the call's channel mode changes.
  useEffect(() => {
    const g = panGraphRef.current;
    if (g) {
      try { g.collectorPan.pan.value = hasDualChannelAudio ? -1 : 0; } catch { /* ignore */ }
    }
  }, [hasDualChannelAudio]);

  // Close the AudioContext when the player unmounts.
  useEffect(() => () => {
    try { void panGraphRef.current?.ctx?.close(); } catch { /* ignore */ }
    panGraphRef.current = null;
  }, []);

  useEffect(() => {
    setCurrentMs(0);
    setIsPlaying(false);
    setFollowPlayback(true);
    setWithinMatches([]);
    setWithinQuery('');
    setPlaybackRate(1);
    setVolume(1);
    setMasterMuted(false);
    setScrubMs(null);
    setTransportMenuAnchor(null);
    setCollectorMuted(false);
    setContactMuted(false);
    setPlaybackAudioSeconds(null);
    const el = audioRef.current;
    if (el) {
      el.pause();
      try { el.currentTime = 0; } catch { /* media not ready */ }
    }
    const contactEl = contactAudioRef.current;
    if (contactEl) {
      contactEl.pause();
      try { contactEl.currentTime = 0; } catch { /* media not ready */ }
    }
    return () => {
      audioRef.current?.pause();
      contactAudioRef.current?.pause();
    };
  }, [callId]);

  const collectorRecId = collectorRecording?.id;
  const remoteRecId = remoteRecording?.id;
  const mixedRecId = mixedRecording?.id;
  useEffect(() => {
    const objectUrls: string[] = [];
    let cancelled = false;
    const loadForCallId = String(callId || '');
    async function loadAudio() {
      setAudioError(null);
      setAudioUrl(null);
      setContactUrl(null);
      if (!canListen || !call?.has_recording || !loadForCallId) return;
      if (String(call.id) !== loadForCallId) return;
      if (process.env.NODE_ENV === 'test') {
        setAudioUrl('blob:mock-audio');
        if (hasDualChannelAudio) setContactUrl('blob:mock-audio-contact');
        return;
      }
      try {
        if (hasDualChannelAudio && collectorRecId && remoteRecId) {
          // Two genuinely separate media files — stream each source independently.
          const [collectorRes, remoteRes] = await Promise.all([
            apiClient.callQa.streamRecordingSource(loadForCallId, String(collectorRecId)),
            apiClient.callQa.streamRecordingSource(loadForCallId, String(remoteRecId)),
          ]);
          const cUrl = URL.createObjectURL(collectorRes.data);
          const rUrl = URL.createObjectURL(remoteRes.data);
          objectUrls.push(cUrl, rUrl);
          if (!cancelled && String(callId) === loadForCallId) {
            setAudioUrl(cUrl);
            setContactUrl(rUrl);
          }
        } else if (mixedRecId && separatedLegsTooShort) {
          const response = await apiClient.callQa.streamRecordingSource(loadForCallId, String(mixedRecId));
          const url = URL.createObjectURL(response.data);
          objectUrls.push(url);
          if (!cancelled && String(callId) === loadForCallId) setAudioUrl(url);
        } else {
          const response = await apiClient.callQa.streamRecording(loadForCallId);
          const url = URL.createObjectURL(response.data);
          objectUrls.push(url);
          if (!cancelled && String(callId) === loadForCallId) setAudioUrl(url);
        }
      } catch {
        if (!cancelled && String(callId) === loadForCallId) {
          setAudioError('Unable to load recording for playback.');
        }
      }
    }
    void loadAudio();
    return () => {
      cancelled = true;
      objectUrls.forEach((u) => URL.revokeObjectURL(u));
      audioRef.current?.pause();
      contactAudioRef.current?.pause();
    };
  }, [
    canListen,
    call?.has_recording,
    call?.id,
    callId,
    hasDualChannelAudio,
    collectorRecId,
    remoteRecId,
    mixedRecId,
    separatedLegsTooShort,
    captureMode,
  ]);

  useEffect(() => {
    if (!audioUrl || !audioRef.current || !Number.isFinite(initialSeek) || initialSeek <= 0) return;
    const el = audioRef.current;
    const seek = () => {
      el.currentTime = initialSeek / 1000;
      setCurrentMs(initialSeek);
      if (hasDualChannelAudio) {
        dualChannelSeek({ collector: el, contact: contactAudioRef.current }, initialSeek / 1000, contactOffsetSec);
      }
      const playResult = el.play?.();
      if (playResult && typeof (playResult as Promise<void>).catch === 'function') {
        void (playResult as Promise<void>).catch(() => undefined);
      }
    };
    if (el.readyState >= 1) seek();
    else el.addEventListener('loadedmetadata', seek, { once: true });
  }, [audioUrl, initialSeek, hasDualChannelAudio, contactOffsetSec]);

  const applyPlaybackRate = useCallback((rate: number) => {
    setPlaybackRate(rate);
    if (audioRef.current) audioRef.current.playbackRate = rate;
    if (hasDualChannelAudio) {
      dualChannelSetPlaybackRate(
        { collector: audioRef.current, contact: contactAudioRef.current },
        rate,
      );
    }
  }, [hasDualChannelAudio]);

  const seekPlayback = useCallback((ms: number) => {
    const el = audioRef.current;
    const safeMs = Math.max(0, ms);
    setCurrentMs(safeMs);
    if (!el) return;
    const targetSec = safeMs / 1000;
    if (hasDualChannelAudio) {
      dualChannelSeek({ collector: el, contact: contactAudioRef.current }, targetSec, contactOffsetSec);
    } else {
      el.currentTime = targetSec;
    }
  }, [hasDualChannelAudio, contactOffsetSec]);

  const togglePlayback = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    const pair = { collector: el, contact: contactAudioRef.current };
    if (!el.paused) {
      if (hasDualChannelAudio) dualChannelPause(pair);
      else el.pause();
      return;
    }
    if (hasDualChannelAudio) {
      void dualChannelPlay(pair);
      return;
    }
    const playResult = el.play?.();
    if (playResult && typeof (playResult as Promise<void>).catch === 'function') {
      void (playResult as Promise<void>).catch(() => undefined);
    }
  }, [hasDualChannelAudio]);

  const toggleMasterMute = useCallback(() => {
    if (masterMuted || volume === 0) {
      setMasterMuted(false);
      if (volume === 0) setVolume(1);
      return;
    }
    setMasterMuted(true);
  }, [masterMuted, volume]);

  useEffect(() => {
    const level = masterMuted ? 0 : volume;
    if (audioRef.current) audioRef.current.volume = level;
    if (contactAudioRef.current) contactAudioRef.current.volume = level;
  }, [volume, masterMuted, audioUrl, contactUrl]);

  const tickFromMedia = useCallback((el: HTMLMediaElement) => {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (process.env.NODE_ENV !== 'test' && now - lastTickRef.current < 80) return;
    lastTickRef.current = now;
    const ms = Math.floor(el.currentTime * 1000);
    setCurrentMs(ms);
    if (hasDualChannelAudio) {
      correctDualChannelDrift(
        { collector: audioRef.current, contact: contactAudioRef.current },
        'collector',
        40,
        contactOffsetSec,
      );
    }
  }, [hasDualChannelAudio, contactOffsetSec]);

  const seekTo = useCallback((ms: number, _segmentId?: string | null) => {
    const el = audioRef.current;
    const targetSec = Math.max(0, ms) / 1000;
    setCurrentMs(Math.max(0, ms));
    setFollowPlayback(true);
    if (!el) return;
    if (hasDualChannelAudio) {
      dualChannelSeek({ collector: el, contact: contactAudioRef.current }, targetSec, contactOffsetSec);
      void dualChannelPlay({ collector: el, contact: contactAudioRef.current });
    } else {
      el.currentTime = targetSec;
      const playResult = el.play?.();
      if (playResult && typeof (playResult as Promise<void>).catch === 'function') {
        void (playResult as Promise<void>).catch(() => undefined);
      }
    }
  }, [hasDualChannelAudio, contactOffsetSec]);

  const activeSegments = useMemo(
    () => findActiveSegments(segments, currentMs),
    [segments, currentMs],
  );
  const activeSegmentIds = useMemo(
    () => new Set(activeSegments.map((s) => s.id)),
    [activeSegments],
  );
  const activeRoles = useMemo(() => activeSpeakerRoles(activeSegments), [activeSegments]);
  const speakerStatus = useMemo(
    () => playbackSpeakerStatus({ currentMs, playing: isPlaying, activeRoles }),
    [currentMs, isPlaying, activeRoles],
  );

  const activeKey = activeSegments.map((s) => s.id).join(',');
  useEffect(() => {
    if (!followPlayback || activeSegments.length === 0) return;
    const first = activeSegments[0];
    const node = segmentElRefs.current[first.id];
    if (!node) return;
    suppressAutoScrollRef.current = true;
    if (typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: process.env.NODE_ENV === 'test' ? 'auto' : 'smooth', block: 'center' });
    }
    const t = window.setTimeout(() => {
      suppressAutoScrollRef.current = false;
    }, 450);
    return () => window.clearTimeout(t);
  }, [followPlayback, activeKey]); // eslint-disable-line react-hooks/exhaustive-deps -- activeKey captures segment identity

  const onTranscriptScroll = () => {
    if (suppressAutoScrollRef.current) return;
    if (followPlayback) setFollowPlayback(false);
  };

  const scorePayload = scoreData?.data?.data;
  const score = scorePayload?.score;
  const scoreCriteria = score?.criteria || [];
  const criticalCriteria = scoreCriteria.filter((c: any) => c.is_critical_failure);
  const callFlags = scorePayload?.flags || [];
  // Axios → { data: { success, data: { notes, summary } } }; some test caches nest one level deeper.
  const notesPayloadRaw = activityNotesData?.data?.data;
  const notesPayload = (notesPayloadRaw?.notes || notesPayloadRaw?.summary)
    ? notesPayloadRaw
    : (notesPayloadRaw?.data?.notes || notesPayloadRaw?.data?.summary)
      ? notesPayloadRaw.data
      : notesPayloadRaw;
  const activityNotes = notesPayload?.notes || [];
  const reviewSummary = notesPayload?.summary;
  const activityNotesLoaded = Boolean(notesPayload);

  if (isLoading || (callId && !call && !error)) return <CircularProgress size={24} />;
  if (error || !call) return <Alert severity="error">Call not found or access denied.</Alert>;

  const transcriptionStatus = call.transcription_status || transcriptPayload?.transcription_status || 'NOT_REQUESTED';
  const transcriptError = transcriptPayload?.transcript?.error_code;

  // Automated analysis status governs whether we may claim the call is CLEAR.
  const analysisStatus = String(call.analysis_status || 'NOT_ANALYZED').toUpperCase();
  const analysisComplete = analysisStatus === 'COMPLETE';
  const classification = String(call.classification || 'UNKNOWN').toUpperCase();
  const callSummary = call.summary && typeof call.summary === 'object' ? call.summary : null;
  const providerBlocked = String(transcriptError || '').includes('PROVIDER_NOT_CONFIGURED')
    || String(transcriptError || '').includes('OPENAI_API_KEY');
  const analysisStatusLabel = providerBlocked && analysisStatus !== 'COMPLETE'
    ? 'PROVIDER NOT CONFIGURED'
    : analysisStatus.replace(/_/g, ' ');
  const classificationLabelMap: Record<string, string> = {
    RIGHT_PARTY: 'Right party',
    THIRD_PARTY: 'Third party',
    UNCLEAR: 'Unclear',
    UNKNOWN: 'Unknown',
  };
  const openFlaggedNotes = activityNotes.filter((n: any) => {
    const status = String(n.review_status || n.status || 'OPEN').toUpperCase();
    if (status === 'DISMISSED') return false;
    const sev = String(n.severity || '').toLowerCase();
    return sev === 'warning' || sev === 'serious' || sev === 'critical' || sev === 'info' || sev === 'note';
  });
  const liveFindingCounts = {
    notes: Number(reviewSummary?.note_count ?? call.note_count ?? 0),
    warning: Number(reviewSummary?.warning_count ?? call.warning_count ?? 0),
    serious: Number(reviewSummary?.serious_count ?? call.serious_count ?? 0),
    critical: Number(reviewSummary?.critical_count ?? call.critical_count ?? 0),
  };
  // Hero chips must match the cards: tally open activity notes (flags + projected scorecard fails).
  // Prefer that once notes have loaded — call.serious_count alone can be 0 when cards still show.
  const talliedFromNotes = openFlaggedNotes.reduce(
    (acc: typeof liveFindingCounts, n: any) => {
      const sev = String(n.severity || '').toLowerCase();
      if (sev === 'critical') acc.critical += 1;
      else if (sev === 'serious') acc.serious += 1;
      else if (sev === 'warning') acc.warning += 1;
      else if (sev === 'info' || sev === 'note') acc.notes += 1;
      return acc;
    },
    { notes: 0, warning: 0, serious: 0, critical: 0 },
  );
  const findingCounts = activityNotesLoaded ? talliedFromNotes : liveFindingCounts;
  const summaryThemes = openFlaggedNotes
    .filter((n: any) => ['warning', 'serious', 'critical'].includes(String(n.severity || '').toLowerCase()))
    .map((n: any) => String(n.title || n.category || '').trim())
    .filter(Boolean);
  const transcriptHighlights = highlightTranscriptByFindings(segments, activityNotes, {
    analysisComplete,
  });
  const transcriptHasFindingMarks = Object.keys(transcriptHighlights).length > 0;
  const displayNarrative = buildCallSummaryNarrative({
    // Prefer the AI scoring narrative (Ignite-style factual paragraph) when present;
    // otherwise fall back to any stored narrative, then to derived prose.
    storedNarrative: (score?.calculation_detail?.summary as string)
      || (callSummary?.narrative as string)
      || null,
    classification,
    disclosureStatus: call.disclosure_status,
    analysisComplete,
    counts: findingCounts,
    themes: summaryThemes,
  });
  const activityAroundHref = call.device_id
    ? `/workforce-monitoring/activity?device_id=${encodeURIComponent(call.device_id)}${
      call.employee_id ? `&employee_id=${encodeURIComponent(call.employee_id)}` : ''
    }&from=${encodeURIComponent(moment(call.started_at).subtract(5, 'minutes').toISOString())}&to=${encodeURIComponent(
      moment(call.ended_at || call.started_at).add(5, 'minutes').toISOString(),
    )}`
    : null;

  // Independent Agent Conduct incidents (never derived from the scorecard).
  const conductRaw = (conductData as any)?.data?.data;
  const conductIncidents: any[] = Array.isArray(conductRaw) ? conductRaw : [];
  const conductActive = conductIncidents.filter((i: any) => !i.superseded);
  const processingCtx: CallProcessingCtx = {
    analysisComplete,
    transcriptionStatus,
    qaStatus: String(call.qa_status || ''),
    providerBlocked,
  };
  const conductStatus = conductCallStatus(conductIncidents, processingCtx);
  const scorecardTile = scorecardTileStatus(score, processingCtx);

  // Finding markers mapped onto the call timeline (click to jump).
  const measuredAudioSeconds = playbackAudioSeconds
    ?? (call.audio_duration_seconds != null ? Number(call.audio_duration_seconds) : null);
  const sessionSeconds = Number(call.duration_seconds || 0);
  const audioShorterThanSession = audioMuchShorterThanSession(sessionSeconds, measuredAudioSeconds);
  // Timeline / seek use real audio length when session wall-clock is much longer (incomplete capture).
  const durationMs = Math.max(
    1,
    (audioShorterThanSession && measuredAudioSeconds != null
      ? measuredAudioSeconds
      : (sessionSeconds || measuredAudioSeconds || 0)) * 1000,
  );
  // Transport uses the real media length once the element reports it, so the
  // playhead matches what is actually playing.
  const mediaDurationMs = Math.max(
    1,
    playbackAudioSeconds != null && playbackAudioSeconds > 0
      ? playbackAudioSeconds * 1000
      : durationMs,
  );
  const shownMs = Math.min(mediaDurationMs, Math.max(0, scrubMs ?? currentMs));
  const volumeLevel = masterMuted ? 0 : volume;
  const VolumeIcon = volumeLevel === 0 ? VolumeOffIcon : volumeLevel < 0.5 ? VolumeDownIcon : VolumeUpIcon;
  const timelineMarkers = [
    ...callFlags.map((f: any) => ({
      ms: Number(f.start_ms || 0),
      color: f.severity === 'critical' || f.severity === 'serious' ? 'error.main' : 'warning.main',
      title: `${f.category || 'Flag'}: “${f.matched_phrase || ''}”`,
      segmentId: f.segment_id,
    })),
    ...activityNotes
      .filter((n: any) => n.start_ms != null)
      .map((n: any) => ({
        ms: Number(n.start_ms || 0),
        color: n.severity === 'critical' || n.severity === 'serious' ? 'error.main' : n.severity === 'warning' ? 'warning.main' : 'info.main',
        title: n.title || 'Finding',
        segmentId: n.segment_id,
      })),
  ].filter((m) => Number.isFinite(m.ms) && m.ms >= 0);

  const triggerBlobDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };
  const sanitizeDownloadPart = (raw?: string | null, fallback = 'unknown') => {
    const cleaned = String(raw || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^A-Za-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48);
    return cleaned || fallback;
  };
  /** Agent_Computer_YYYY-MM-DD_HHmm.wav — readable Downloads-folder name. */
  const buildLocalRecordingFilename = (extension = 'wav') => {
    const agent = sanitizeDownloadPart(call.employee_name, 'Unlinked-agent');
    const computer = sanitizeDownloadPart(call.device_name || call.device_id, 'Unknown-computer');
    let when = 'unknown-time';
    if (call.started_at) {
      const d = new Date(call.started_at);
      if (!Number.isNaN(d.getTime())) {
        const iso = d.toISOString();
        when = `${iso.slice(0, 10)}_${iso.slice(11, 13)}${iso.slice(14, 16)}`;
      }
    }
    return `${agent}_${computer}_${when}.${extension}`;
  };
  const filenameFromContentDisposition = (headers?: Record<string, any>) => {
    const cd = headers?.['content-disposition'] || headers?.['Content-Disposition'];
    if (!cd || typeof cd !== 'string') return null;
    const utf = /filename\*=UTF-8''([^;]+)/i.exec(cd);
    if (utf?.[1]) {
      try { return decodeURIComponent(utf[1].trim()); } catch { /* fall through */ }
    }
    const quoted = /filename="([^"]+)"/i.exec(cd);
    if (quoted?.[1]) return quoted[1];
    const bare = /filename=([^;]+)/i.exec(cd);
    return bare?.[1]?.trim().replace(/^"|"$/g, '') || null;
  };
  const downloadCallRecording = async () => {
    try {
      const res = await apiClient.callQa.downloadRecording(String(callId));
      const ext = (call.recording_content_type && call.recording_content_type.includes('ogg')) ? 'ogg' : 'wav';
      const fromHeader = filenameFromContentDisposition((res as any).headers);
      triggerBlobDownload(res.data, fromHeader || buildLocalRecordingFilename(ext));
    } catch {
      setAudioError('Unable to download recording.');
    }
  };

  // Combine the two SEPARATED tracks into one stereo WAV (agent left, customer right).
  const downloadCombinedStereo = async () => {
    try {
      const res = await apiClient.callQa.downloadCombinedRecording(String(callId));
      const fromHeader = filenameFromContentDisposition((res as any).headers);
      triggerBlobDownload(res.data, fromHeader || buildLocalRecordingFilename('wav'));
    } catch {
      setAudioError('Unable to build the combined stereo file.');
    }
  };

  const AudioPlayerCard = (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }} data-testid="call-review-audio-player">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2">Audio Player</Typography>
        <Chip
          size="small"
          data-testid="capture-mode-chip"
          label={captureModeLabel[captureMode] || captureMode}
          color={hasDualChannelAudio ? 'success' : captureMode === 'UNAVAILABLE' ? 'error' : 'default'}
        />
        {canDownload && call.has_recording && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            data-testid="download-recording"
            onClick={hasDualChannelAudio ? downloadCombinedStereo : downloadCallRecording}
          >
            Download
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Box data-testid="active-speaker-indicator">
          {speakerStatus.kind === 'silence' && (
            <Typography variant="body2" color="text.secondary">Silence · {formatTimestamp(currentMs)}</Typography>
          )}
          {speakerStatus.kind === 'paused' && (
            <Typography variant="body2">
              {speakerStatus.roles.map((r) => speakerLabel(r)).join(' + ')} · Paused at {formatTimestamp(speakerStatus.atMs)}
            </Typography>
          )}
          {speakerStatus.kind === 'speaking' && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              {speakerStatus.roles.map((r) => (
                <Chip
                  key={r}
                  size="small"
                  color={speakerColor(r)}
                  label={`${speakerLabel(r)} ● Speaking`}
                  aria-label={`${speakerLabel(r)} currently speaking`}
                />
              ))}
              <Typography variant="caption" color="text.secondary">{formatTimestamp(currentMs)}</Typography>
            </Stack>
          )}
        </Box>
      </Box>

      {/* Finding markers pinned on the call timeline */}
      {timelineMarkers.length > 0 && (
        <Box sx={{ position: 'relative', height: 14, mb: 0.5, mx: 0.5 }}>
          <Box sx={{ position: 'absolute', left: 0, right: 0, top: 6, height: 2, bgcolor: 'divider', borderRadius: 1 }} />
          {timelineMarkers.map((m, i) => (
            <Box
              key={i}
              onClick={() => seekTo(m.ms, m.segmentId)}
              title={m.title}
              sx={{
                position: 'absolute',
                top: 0,
                left: `${Math.min(100, (m.ms / durationMs) * 100)}%`,
                transform: 'translateX(-50%) rotate(45deg)',
                width: 9,
                height: 9,
                borderRadius: '2px',
                bgcolor: m.color,
                boxShadow: '0 0 0 2px #fff',
                cursor: 'pointer',
              }}
            />
          ))}
        </Box>
      )}

      {!hasDualChannelAudio && (
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
          Separate Collector/Contact channels are not available for this recording. Transcript diarization may still label speakers.
        </Typography>
      )}
      {audioShorterThanSession && measuredAudioSeconds != null && (
        <Alert severity="warning" sx={{ mb: 1 }} data-testid="audio-shorter-than-session">
          Available audio is {formatDuration(measuredAudioSeconds)}, but the call session was listed as{' '}
          {formatDuration(sessionSeconds)}. The remote/system channel may be missing, or only part of the
          microphone capture uploaded — check the Agent recording queue on this computer.
        </Alert>
      )}
      {audioError && <Alert severity="error" sx={{ mb: 1 }}>{audioError}</Alert>}
      {canListen && call.has_recording ? (
        audioUrl ? (
          <Box sx={{ position: 'relative' }}>
            <audio
              ref={audioRef}
              preload="metadata"
              src={audioUrl}
              data-testid="call-review-audio"
              muted={hasDualChannelAudio ? collectorMuted : undefined}
              style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
              onLoadedMetadata={(e) => {
                const d = e.currentTarget.duration;
                if (Number.isFinite(d) && d > 0) setPlaybackAudioSeconds(Math.round(d));
              }}
              onTimeUpdate={(e) => tickFromMedia(e.currentTarget)}
              onPlay={() => {
                setIsPlaying(true);
                ensureStereoSplit();
                if (hasDualChannelAudio && contactAudioRef.current) {
                  void dualChannelPlay({ collector: audioRef.current, contact: contactAudioRef.current });
                }
              }}
              onPause={() => {
                setIsPlaying(false);
                if (hasDualChannelAudio) {
                  dualChannelPause({ collector: audioRef.current, contact: contactAudioRef.current });
                }
              }}
              onSeeked={(e) => {
                setCurrentMs(Math.floor(e.currentTarget.currentTime * 1000));
                if (hasDualChannelAudio) {
                  dualChannelSeek(
                    { collector: audioRef.current, contact: contactAudioRef.current },
                    e.currentTarget.currentTime,
                    contactOffsetSec,
                  );
                }
              }}
              onRateChange={(e) => setPlaybackRate(e.currentTarget.playbackRate || 1)}
            >
              Your browser does not support audio playback.
            </audio>
            {/* Contact/Remote track — always mounted (even outside the dual-channel controls) so the
                Web Audio source tap used for stereo-split panning stays valid across calls. */}
            <audio
              ref={contactAudioRef}
              preload="metadata"
              src={contactUrl || undefined}
              muted={contactMuted}
              style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
              data-testid="call-review-audio-contact"
            />
            <Box
              data-testid="call-review-transport"
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                px: 1,
                py: 0.75,
                borderRadius: 999,
                bgcolor: '#f4f6f8',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <IconButton
                size="small"
                aria-label={isPlaying ? 'Pause' : 'Play'}
                data-testid="call-review-play-pause"
                onClick={togglePlayback}
                sx={{ color: 'text.primary' }}
              >
                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
              <Typography
                variant="body2"
                component="span"
                data-testid="call-review-time"
                sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {formatMediaClock(shownMs)}
                </Box>
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {` / ${formatMediaClock(mediaDurationMs)}`}
                </Box>
              </Typography>
              <Slider
                aria-label="Playback progress"
                data-testid="call-review-progress"
                min={0}
                max={mediaDurationMs}
                step={100}
                value={shownMs}
                valueLabelDisplay="auto"
                valueLabelFormat={(ms) => formatMediaClock(ms)}
                getAriaValueText={(ms) => formatMediaClock(ms)}
                onChange={(_, value) => {
                  const ms = Array.isArray(value) ? value[0] : value;
                  setScrubMs(ms);
                }}
                onChangeCommitted={(_, value) => {
                  const ms = Array.isArray(value) ? value[0] : value;
                  seekPlayback(ms);
                  setScrubMs(null);
                }}
                sx={{
                  flex: '1 1 160px',
                  minWidth: 140,
                  mx: 0.5,
                  height: 10,
                  color: 'primary.main',
                  '& .MuiSlider-rail': {
                    height: 10,
                    borderRadius: 999,
                    opacity: 1,
                    backgroundColor: '#d5dbe3',
                  },
                  '& .MuiSlider-track': {
                    height: 10,
                    border: 'none',
                    borderRadius: 999,
                    backgroundColor: 'primary.main',
                  },
                  '& .MuiSlider-thumb': {
                    width: 18,
                    height: 18,
                    boxSizing: 'border-box',
                    backgroundColor: '#fff',
                    border: '3px solid',
                    borderColor: 'primary.main',
                    boxShadow: '0 1px 4px rgba(15, 23, 42, 0.35)',
                    '&:hover, &.Mui-focusVisible': {
                      boxShadow: '0 0 0 6px rgba(25, 118, 210, 0.22)',
                    },
                    '&.Mui-active': {
                      boxShadow: '0 0 0 8px rgba(25, 118, 210, 0.28)',
                    },
                  },
                }}
              />
              <Tooltip title={volumeLevel === 0 ? 'Unmute' : 'Mute'}>
                <IconButton
                  size="small"
                  aria-label={volumeLevel === 0 ? 'Unmute' : 'Mute'}
                  data-testid="call-review-mute"
                  onClick={toggleMasterMute}
                  sx={{ color: 'text.primary' }}
                >
                  <VolumeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Slider
                aria-label="Volume"
                data-testid="call-review-volume"
                min={0}
                max={1}
                step={0.05}
                value={volumeLevel}
                onChange={(_, value) => {
                  const next = Array.isArray(value) ? value[0] : value;
                  setVolume(next);
                  setMasterMuted(next === 0);
                }}
                sx={{
                  width: 72,
                  flex: '0 0 72px',
                  color: 'text.secondary',
                  '& .MuiSlider-thumb': { width: 12, height: 12 },
                  '& .MuiSlider-rail': { opacity: 1, backgroundColor: '#d5dbe3' },
                }}
              />
              <Tooltip title="More playback options">
                <IconButton
                  size="small"
                  aria-label="More playback options"
                  data-testid="call-review-more"
                  onClick={(e) => setTransportMenuAnchor(e.currentTarget)}
                  sx={{ color: 'text.primary' }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={transportMenuAnchor}
                open={Boolean(transportMenuAnchor)}
                onClose={() => setTransportMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Typography variant="caption" sx={{ px: 2, pt: 0.5, pb: 0.25, display: 'block', color: 'text.secondary' }}>
                  Playback speed
                </Typography>
                {[0.75, 1, 1.25, 1.5, 2].map((rate) => (
                  <MenuItem
                    key={rate}
                    selected={playbackRate === rate}
                    onClick={() => {
                      applyPlaybackRate(rate);
                      setTransportMenuAnchor(null);
                    }}
                  >
                    {rate}x
                  </MenuItem>
                ))}
              </Menu>
            </Box>
            {hasDualChannelAudio && (
              <Box sx={{ mt: 1 }} data-testid="dual-channel-controls">
                <Typography variant="caption" display="block" gutterBottom>
                  Two separate recordings — Collector (microphone, left ear) and Remote/System
                  (right ear) — play on one shared clock, split to separate ears to avoid echo.
                  Mute silences a track without stopping it.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    size="small"
                    color={isPlaying && activeRoles.some(isAgentLikeRole) ? 'primary' : 'default'}
                    label={isPlaying && activeRoles.some(isAgentLikeRole) ? 'Collector ACTIVE' : 'Collector'}
                  />
                  <Chip
                    size="small"
                    color={isPlaying && activeRoles.some(isCustomerLikeRole) ? 'secondary' : 'default'}
                    label={isPlaying && activeRoles.some(isCustomerLikeRole) ? 'Contact ACTIVE' : 'Contact'}
                  />
                  <Button size="small" onClick={() => setCollectorMuted((v) => !v)}>
                    {collectorMuted ? 'Unmute Collector' : 'Mute Collector'}
                  </Button>
                  <Button size="small" onClick={() => setContactMuted((v) => !v)}>
                    {contactMuted ? 'Unmute Contact' : 'Mute Contact'}
                  </Button>
                </Stack>
              </Box>
            )}
            <FormControl size="small" sx={{ mt: 1, minWidth: 120 }}>
              <InputLabel id="playback-speed-label">Speed</InputLabel>
              <Select
                labelId="playback-speed-label"
                label="Speed"
                value={playbackRate}
                data-testid="playback-rate"
                onChange={(e) => applyPlaybackRate(Number(e.target.value))}
              >
                {[0.75, 1, 1.25, 1.5, 2].map((r) => (
                  <MenuItem key={r} value={r}>{r}x</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        ) : (
          <CircularProgress size={20} />
        )
      ) : (
        <Typography variant="body2" color="text.secondary">
          {call.has_recording ? 'Listening requires call_qa.recordings.listen.' : 'No recording available.'}
        </Typography>
      )}
      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
        {call.recording_original_filename || '—'} · {call.recording_content_type || '—'} · {call.recording_size_bytes ?? 0} bytes
      </Typography>
    </Paper>
  );

  return (
    <Box>
      {!embedded && (
        <Button size="small" onClick={() => navigate('/call-qa/calls')} sx={{ mb: 1 }}>← Back to Calls</Button>
      )}

      {/* ===== HERO ===== */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }} data-testid="call-review-summary">
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          <Box
            sx={{
              width: 48, height: 48, borderRadius: 2, flex: '0 0 auto',
              background: 'linear-gradient(135deg,#4f46e5,#0d9488)', color: '#fff',
              display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 16,
            }}
          >
            {callInitials(call.employee_name || call.customer_name || call.customer_phone)}
          </Box>
          <Box sx={{ minWidth: 200 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {call.employee_name || (call.unlinked ? 'Unlinked agent' : 'Agent')}
              {call.unlinked && <Chip size="small" color="warning" label="UNLINKED" />}
              <Chip
                size="small"
                color={classificationChip(call.classification).color}
                variant={classificationChip(call.classification).color === 'default' ? 'outlined' : 'filled'}
                label={classificationChip(call.classification).label}
              />
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {moment(call.started_at).format('MMM D, YYYY h:mm A')} · {formatCallDurationLabel(call.duration_seconds, measuredAudioSeconds)} · {call.direction} · {call.recording_source}
            </Typography>
            {(call.campaign_name || call.service_name) && (
              <Typography variant="body2" color="text.secondary" data-testid="hero-campaign-service">
                {[call.campaign_name, call.service_name].filter(Boolean).join(' · ')}
              </Typography>
            )}
            {/* Who + where + what system this call was on. */}
            <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 0.75 }}>
              <Chip
                size="small"
                variant="outlined"
                icon={<PersonIcon fontSize="small" />}
                label={`Customer: ${call.customer_name || call.customer_phone || call.account_number || 'Unidentified'}`}
              />
              <Chip
                size="small"
                variant="outlined"
                icon={<ComputerIcon fontSize="small" />}
                label={call.device_name || call.device_id || 'Unknown computer'}
              />
              {friendlyCommSystem(call) && (
                <Chip
                  size="small"
                  variant="outlined"
                  color="primary"
                  icon={<PhoneIcon fontSize="small" />}
                  label={friendlyCommSystem(call)}
                />
              )}
            </Box>
            {/* Collections outcomes detected on the call: promise-to-pay, payment taken, dispute. */}
            {(() => {
              const o = (callSummary as any)?.outcomes;
              if (!o || (!o.ptp && !o.payment_taken && !o.disputed)) return null;
              const money = (n: any) => (n != null && Number.isFinite(Number(n))
                ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
                : null);
              return (
                <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 0.75 }}>
                  {o.ptp && (
                    <Chip size="small" color="info" variant="filled"
                      label={`Promise to pay${money(o.ptp_amount) ? ` · ${money(o.ptp_amount)}` : ''}`} />
                  )}
                  {o.payment_taken && (
                    <Chip size="small" color="success" variant="filled"
                      label={`Payment taken${money(o.payment_amount) ? ` · ${money(o.payment_amount)}` : ''}`} />
                  )}
                  {o.disputed && (
                    <Chip size="small" color="warning" variant="filled" label="Disputed" />
                  )}
                </Box>
              );
            })()}
          </Box>
          {canUpdate && (
            <Box sx={{ ml: 'auto' }}>
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <Select
                  value={call.disposition || 'UNKNOWN'}
                  onChange={(e) => updateDisposition.mutate(String(e.target.value))}
                >
                  {['UNKNOWN', 'NO_ANSWER', 'VOICEMAIL', 'CONNECTED', 'RIGHT_PARTY', 'UNCLEAR', 'OTHER'].map((d) => (
                    <MenuItem key={d} value={d}>{d.replace(/_/g, ' ')}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>

        <Typography sx={{ mt: 1.5, mb: 1.5, fontSize: '0.95rem', lineHeight: 1.6 }} data-testid="call-hero-narrative">
          {displayNarrative}
        </Typography>

        {/* Three independent measurements: Conduct (misconduct), Scorecard (quality), Review (action). */}
        <Grid container spacing={1.5} data-testid="call-measurements">
          <Grid item xs={12} sm={4}>
            <MeasurementTile
              testid="summary-conduct"
              heading="Agent Conduct"
              value={conductStatus.label}
              color={conductToneColor(conductStatus.tone)}
              sub={conductActive.length > 0 ? `${conductActive.length} finding${conductActive.length === 1 ? '' : 's'} · requires human review` : 'Independent of QA score'}
              onClick={() => setActiveTab('conduct')}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <MeasurementTile
              testid="summary-scorecard"
              heading="QA Scorecard"
              value={scorecardTile.score != null ? (
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <ScoreRing value={scorecardTile.score} word={scorecardTile.word} color={scorecardTile.color} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0, flex: 1 }}>
                    {score?.automated_overall_score != null && (
                      <Typography component="span" sx={{ fontSize: '0.78rem', fontWeight: 400, color: 'text.secondary', lineHeight: 1.3 }}>
                        Automated: {score.automated_overall_score}%{score.automated_overall_status ? ` · ${String(score.automated_overall_status)}` : ''}
                      </Typography>
                    )}
                    <Typography component="span" sx={{ fontSize: '0.78rem', fontWeight: 400, color: 'text.secondary', lineHeight: 1.3 }}>
                      Review: {reviewStatusLabel(score?.review_status)}
                    </Typography>
                    {(canRunScore || isSuperUser) && (
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                        {canRunScore && (
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: '0.72rem', lineHeight: 1.2 }}
                            disabled={requestScoring.isPending}
                            onClick={(e) => { e.stopPropagation(); requestScoring.mutate({ force: true }); }}
                          >
                            {requestScoring.isPending ? 'Scoring…' : 'Re-score'}
                          </Button>
                        )}
                        {isSuperUser && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: '0.72rem', lineHeight: 1.2 }}
                            disabled={reanalyze.isPending}
                            onClick={(e) => { e.stopPropagation(); reanalyze.mutate(); }}
                          >
                            {reanalyze.isPending ? 'Re-analyzing…' : 'Re-analyze'}
                          </Button>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              ) : scorecardTile.value}
              color={scorecardTile.color}
              sub={score?.scorecard_version_label ? String(score.scorecard_version_label) : 'A low score is not misconduct'}
              onClick={() => setActiveTab('scorecard')}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <MeasurementTile
              testid="summary-review"
              heading="Review Status"
              value={reviewStatusLabel(score?.review_status)}
              sub={`Analysis: ${analysisStatusLabel}`}
            />
          </Grid>
        </Grid>

        {providerBlocked && (
          <Alert severity="warning" sx={{ mt: 1.5 }} data-testid="provider-not-configured-alert">
            Transcription / AI QA provider is not configured. Deterministic review still works when a transcript exists;
            AI transcription and AI scoring remain unavailable until OPENAI credentials are supplied.
          </Alert>
        )}

        {activityAroundHref && (
          <Box mt={1.5} data-testid="screen-evidence-panel">
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Screen Evidence — correlated screenshots / computer activity for this call window (not a separate screen recording).
            </Typography>
            <Button size="small" variant="outlined" href={activityAroundHref} onClick={(e) => {
              e.preventDefault();
              navigate(activityAroundHref);
            }}>
              Open Screen Evidence (Screenshots / Computer Activity)
            </Button>
          </Box>
        )}
      </Paper>

      {/* ===== PLAYER (top) ===== */}
      {AudioPlayerCard}

      {/* ===== TABS ===== */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Conduct" value="conduct" data-testid="tab-conduct" />
          <Tab label="Scorecard" value="scorecard" data-testid="tab-scorecard" />
          <Tab label="Transcript" value="transcript" data-testid="tab-transcript" />
          <Tab label="Call Details" value="details" data-testid="tab-details" />
        </Tabs>
      </Box>

      {/* ---- Conduct tab: independent Agent Conduct incidents only (never scorecard findings) ---- */}
      <Box hidden={activeTab !== 'conduct'} data-testid="call-conduct-tab">
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }} data-testid="conduct-panel">
          <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
            <Typography variant="subtitle2">Agent Conduct</Typography>
            <Chip
              size="small"
              color={conductToneColor(conductStatus.tone)}
              variant={conductStatus.tone === 'none' || conductStatus.tone === 'pending' ? 'outlined' : 'filled'}
              label={conductStatus.label}
              data-testid="conduct-status-chip"
            />
          </Box>
          <Alert severity="info" sx={{ mb: 1.5 }}>
            These are AI-detected conduct concerns that require human verification. They are independent of
            the QA scorecard — a low score is not misconduct.
          </Alert>

          {conductActive.length === 0 ? (
            conductStatus.tone === 'none' ? (
              <Box display="flex" alignItems="center" gap={1.25} data-testid="conduct-none">
                <VerifiedUserIcon color="success" fontSize="small" />
                <Typography sx={{ fontSize: '0.95rem' }} color="success.dark" fontWeight={600}>
                  No conduct findings on this call.
                </Typography>
              </Box>
            ) : (
              <Box data-testid="conduct-pending">
                <Typography sx={{ fontSize: '0.95rem' }} color="text.secondary">
                  {conductStatus.label}.
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                  {/* An action only where the existing workflow supports it. */}
                  {canUpdate && transcriptionStatus === 'FAILED' && (
                    <Button size="small" variant="outlined" disabled={requestTranscription.isPending}
                      onClick={() => requestTranscription.mutate(true)}>Retry transcription</Button>
                  )}
                  {isSuperUser && transcriptionStatus === 'COMPLETED' && (
                    <Button size="small" variant="outlined" color="secondary" disabled={reanalyze.isPending}
                      onClick={() => reanalyze.mutate()}>
                      {reanalyze.isPending ? 'Re-analyzing…' : 'Run analysis (super user)'}
                    </Button>
                  )}
                </Box>
              </Box>
            )
          ) : conductActive.map((inc: any) => {
            const sev = String(inc.severity || 'warning').toLowerCase();
            const accent = sev === 'critical' || sev === 'serious' ? 'error.main' : 'warning.main';
            return (
              <Paper
                key={inc.id}
                variant="outlined"
                data-testid={`conduct-incident-${inc.id}`}
                sx={{ mb: 1.5, p: 1.75, borderRadius: 2, borderLeft: 4, borderLeftColor: accent }}
              >
                <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={0.5}>
                  <Chip size="small" color={conductToneColor(sev)} label={sev} />
                  <Typography variant="subtitle2">{inc.category_label || inc.category_key}</Typography>
                  <Box sx={{ flex: 1 }} />
                  <Chip size="small" variant="outlined" label={reviewStatusLabel(inc.review_status)} data-testid={`conduct-review-status-${inc.id}`} />
                </Box>
                {inc.explanation && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{inc.explanation}</Typography>
                )}
                {inc.evidence_text && (
                  <Typography variant="body2" sx={{ mb: 0.5, pl: 1.5, borderLeft: 2, borderColor: 'divider', fontStyle: 'italic' }}>
                    “{inc.evidence_text}”
                  </Typography>
                )}
                <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" sx={{ mb: 0.5 }}>
                  {inc.confidence != null && (
                    <Typography variant="caption" color="text.secondary">
                      AI confidence {Math.round(Number(inc.confidence) * 100)}%
                    </Typography>
                  )}
                  {inc.ambiguous && (
                    <Chip size="small" variant="outlined" color="warning" label="Speaker attribution uncertain" />
                  )}
                  {inc.start_ms != null && (
                    <Button size="small" data-testid={`conduct-seek-${inc.id}`} onClick={() => seekTo(inc.start_ms, null)}>
                      Click to Hear {formatTimestamp(inc.start_ms)}
                    </Button>
                  )}
                </Box>
                {canUpdate && (
                  <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
                    <Button size="small" disabled={updateConductIncident.isPending}
                      onClick={() => updateConductIncident.mutate({ id: inc.id, review_status: 'IN_REVIEW' })}>Review</Button>
                    <Button size="small" color="error" disabled={updateConductIncident.isPending}
                      onClick={() => updateConductIncident.mutate({ id: inc.id, review_status: 'CONFIRMED' })}>Confirm</Button>
                    <Button size="small" disabled={updateConductIncident.isPending}
                      onClick={() => updateConductIncident.mutate({ id: inc.id, review_status: 'DISMISSED' })}>Dismiss</Button>
                    <Button size="small" disabled={updateConductIncident.isPending}
                      onClick={() => updateConductIncident.mutate({ id: inc.id, review_status: 'COACHING_REQUIRED' })}>Coaching required</Button>
                    {canManageInvestigations && (
                      <Button size="small" variant="outlined" disabled={createInvestigation.isPending}
                        onClick={() => createInvestigation.mutate()}>Create Investigation</Button>
                    )}
                    {canManageCoaching && call.employee_id && (
                      <Button size="small" variant="outlined" disabled={createCoaching.isPending}
                        onClick={() => createCoaching.mutate()}>Create Coaching</Button>
                    )}
                  </Box>
                )}
              </Paper>
            );
          })}
        </Paper>
      </Box>

      {/* ---- Transcript tab ---- */}
      <Box hidden={activeTab !== 'transcript'}>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Search Within Call</Typography>
          {!canReadTranscript ? (
            <Alert severity="warning">Transcript search requires call_qa.transcripts.read.</Alert>
          ) : (
            <>
              <Box display="flex" gap={1} mb={1}>
                <TextField
                  size="small"
                  fullWidth
                  label="Phrase"
                  value={withinQuery}
                  onChange={(e) => setWithinQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && withinQuery.trim()) searchWithin.mutate(withinQuery.trim());
                  }}
                />
                <Button
                  variant="outlined"
                  disabled={!withinQuery.trim() || searchWithin.isPending}
                  onClick={() => searchWithin.mutate(withinQuery.trim())}
                >
                  Search
                </Button>
              </Box>
              {withinMatches.map((m: any) => (
                <Box key={m.segment_id} sx={{ mb: 1 }}>
                  <Button size="small" onClick={() => seekTo(m.seek_ms)}>
                    {formatTimestamp(m.start_ms)} {m.speaker_role}
                  </Button>
                  <Typography variant="body2" component="span"> {m.excerpt}</Typography>
                </Box>
              ))}
            </>
          )}
        </Paper>

        <Paper
          variant="outlined"
          sx={{ p: 2, maxHeight: 560, overflow: 'auto' }}
          data-testid="transcript-panel"
          ref={transcriptScrollRef}
          onScroll={onTranscriptScroll}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1} flexWrap="wrap">
            <Box>
              <Typography variant="subtitle2">Transcript</Typography>
              {segments.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {transcriptMeta.turns} turns · collector {transcriptMeta.collectorS}s · contact {transcriptMeta.contactS}s
                  {transcriptProvider ? ` · via ${transcriptProvider}` : ''}
                </Typography>
              )}
              {transcriptHasFindingMarks && (
                <Stack direction="row" spacing={1.25} sx={{ mt: 0.5 }} data-testid="transcript-severity-legend">
                  {(['warning', 'serious', 'critical'] as const).map((severity) => (
                    <Typography
                      key={severity}
                      variant="caption"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, textTransform: 'capitalize' }}
                    >
                      <Box
                        component="span"
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: 0.5,
                          bgcolor: TRANSCRIPT_SEVERITY_STYLE[severity].swatch,
                          display: 'inline-block',
                        }}
                      />
                      {severity}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              {!followPlayback && (
                <Button
                  size="small"
                  variant="outlined"
                  data-testid="follow-playback"
                  onClick={() => setFollowPlayback(true)}
                >
                  Follow Playback
                </Button>
              )}
              {followPlayback && (
                <Chip size="small" color="success" label="Follow Playback ON" data-testid="follow-playback-on" />
              )}
            </Stack>
          </Box>
          {!canReadTranscript ? (
            <Alert severity="warning">Transcript requires call_qa.transcripts.read.</Alert>
          ) : transcriptLoading || transcriptFetching ? (
            <CircularProgress size={20} />
          ) : segments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {transcriptionStatus === 'NO_SPEECH'
                ? 'No usable speech detected in this recording.'
                : transcriptionStatus === 'COMPLETED' ? 'No segments.' : 'No transcript yet.'}
            </Typography>
          ) : (
            segments.map((seg: any) => {
              const isActive = activeSegmentIds.has(seg.id);
              const findingSpans = transcriptHighlights[seg.id] || [];
              const findingSeverity = highestHighlightSeverity(findingSpans);
              const agent = isAgentLikeRole(seg.speaker_role);
              const customer = isCustomerLikeRole(seg.speaker_role);
              // Chat-conversation layout: Collector (agent) on the left, Contact (customer)
              // on the right, System centered.
              const align = customer ? 'flex-end' : agent ? 'flex-start' : 'center';
              const bg = agent ? '#edf6ef' : customer ? '#e9f1fc' : '#f1f3f7';
              const bd = isActive
                ? 'primary.main'
                : findingSeverity
                  ? TRANSCRIPT_SEVERITY_STYLE[findingSeverity].swatch
                  : agent ? '#cfe3d2' : customer ? '#d3e3f7' : '#e2e6ee';
              const labelColor = agent ? 'primary.main' : customer ? 'secondary.main' : 'text.secondary';
              return (
                <Box key={seg.id} sx={{ display: 'flex', justifyContent: align, mb: 0.5 }}>
                  <Box
                    ref={(node: HTMLDivElement | null) => {
                      segmentElRefs.current[seg.id] = node;
                    }}
                    data-testid={`transcript-segment-${seg.id}`}
                    data-active={isActive ? 'true' : 'false'}
                    data-finding-severity={findingSeverity || undefined}
                    aria-current={isActive ? 'true' : undefined}
                    onClick={() => seekTo(seg.start_ms, seg.id)}
                    sx={{
                      maxWidth: '78%',
                      minWidth: 110,
                      py: 0.6,
                      px: 1.1,
                      border: 1,
                      borderColor: bd,
                      bgcolor: bg,
                      borderRadius: 1.5,
                      borderTopLeftRadius: agent ? 0.5 : 1.5,
                      borderTopRightRadius: customer ? 0.5 : 1.5,
                      boxShadow: isActive ? 2 : 0,
                      cursor: 'pointer',
                      transition: 'box-shadow 120ms ease, border-color 120ms ease',
                      '& .seg-edit': { opacity: 0, transition: 'opacity .15s ease' },
                      '&:hover .seg-edit': { opacity: 1 },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        flexDirection: customer ? 'row-reverse' : 'row',
                      }}
                    >
                      <Typography component="span" sx={{ fontSize: 10, fontWeight: 800, letterSpacing: '.03em', textTransform: 'uppercase', color: labelColor, lineHeight: 1.4 }}>
                        {speakerLabel(seg.speaker_role)}
                      </Typography>
                      <Typography
                        component="span"
                        onClick={(e: any) => { e.stopPropagation(); seekTo(seg.start_ms, seg.id); }}
                        sx={{ fontSize: 10, color: 'text.secondary', fontVariantNumeric: 'tabular-nums', cursor: 'pointer' }}
                      >
                        {formatTimestamp(seg.start_ms)}
                      </Typography>
                      {isActive && (
                        <Chip
                          size="small"
                          color="primary"
                          label={isPlaying ? 'Speaking' : 'Active'}
                          data-testid={`active-marker-${seg.id}`}
                          sx={{ height: 14, '& .MuiChip-label': { px: 0.6, fontSize: 9.5 } }}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ fontSize: 13.5, lineHeight: 1.35 }}>
                      <HighlightedTranscriptText text={String(seg.text || '')} spans={findingSpans} />
                    </Typography>
                    {canUpdate && (
                      <Box className="seg-edit" sx={{ mt: 0.5, textAlign: customer ? 'right' : 'left' }}>
                        <Select
                          size="small"
                          variant="standard"
                          value={seg.speaker_role}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => correctSpeaker.mutate({
                            segmentId: seg.id,
                            speaker_role: String(e.target.value),
                          })}
                          sx={{ fontSize: 10.5 }}
                        >
                          <MenuItem value="AGENT">Collector (Agent)</MenuItem>
                          <MenuItem value="CUSTOMER">Contact (Customer)</MenuItem>
                          <MenuItem value="SYSTEM">System / IVR</MenuItem>
                          <MenuItem value="UNKNOWN">Unknown</MenuItem>
                        </Select>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })
          )}
        </Paper>
      </Box>

      {/* ---- QA & Findings tab ---- */}
      <Box hidden={activeTab !== 'scorecard'} data-testid="call-scorecard-tab">
        {(() => {
          const disc = disclosureChip(call.disclosure_status);
          const discColor: 'default' | 'success' | 'error' = disc?.tone === 'present' ? 'success' : disc?.tone === 'missing' ? 'error' : 'default';
          return (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2, borderLeft: 3, borderLeftColor: 'divider' }} data-testid="scorecard-disclosure">
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="body2" sx={{ fontWeight: 700 }}>Required Disclosure / Compliance</Typography>
                <Chip size="small" variant={discColor === 'default' ? 'outlined' : 'filled'} color={discColor} label={disc ? disc.label : (call.disclosure_status || 'Not applicable')} />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  Part of the campaign/service scorecard — not a separate call status.
                </Typography>
              </Box>
            </Paper>
          );
        })()}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="activity-notes-panel">
          <Typography variant="subtitle2" gutterBottom>Scorecard Findings</Typography>
          {activityNotes.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No findings for this call.</Typography>
          ) : activityNotes.map((n: any) => {
            const sev = String(n.severity || 'note').toLowerCase();
            const accent = sev === 'critical' || sev === 'serious'
              ? 'warning.main'
              : sev === 'warning'
                ? 'warning.light'
                : 'divider';
            return (
              <Paper
                key={n.id}
                variant="outlined"
                data-testid={`finding-card-${n.id}`}
                sx={{
                  mb: 1.25,
                  p: 1.5,
                  borderRadius: 2,
                  borderLeft: 3,
                  borderLeftColor: accent,
                }}
              >
                <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={0.75}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    letterSpacing={0.4}
                    color="text.secondary"
                    sx={{ textTransform: 'uppercase' }}
                  >
                    {sev}
                  </Typography>
                  <Chip size="small" label={String(n.category || n.type || 'finding').replace(/_/g, ' ')} />
                  <Chip size="small" label={n.created_by || 'SYSTEM'} />
                  <Chip size="small" label={n.review_status || n.status || 'OPEN'} />
                </Box>
                <Typography variant="subtitle2">{n.title}</Typography>
                {n.transcript_excerpt && (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.75,
                      mb: 0.5,
                      fontStyle: 'italic',
                      pl: 1.5,
                      borderLeft: 2,
                      borderColor: 'divider',
                    }}
                  >
                    “{n.transcript_excerpt}”
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {n.description}
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" mt={0.75}>
                  {n.start_ms != null && (
                    <Button
                      size="small"
                      data-testid={`click-to-hear-note-${n.id}`}
                      onClick={() => seekTo(n.start_ms, n.segment_id)}
                    >
                      Click to Hear {formatTimestamp(n.start_ms)}
                    </Button>
                  )}
                  {n.speaker_role && (
                    <Chip size="small" label={speakerLabel(n.speaker_role)} color={speakerColor(n.speaker_role)} />
                  )}
                </Box>
                {canUpdate && !String(n.id).startsWith('criterion:') && (
                  <Box display="flex" gap={1} mt={1}>
                    <Button size="small" onClick={() => reviewNote.mutate({ id: n.id, status: 'CONFIRMED' })}>Confirm</Button>
                    <Button size="small" onClick={() => reviewNote.mutate({ id: n.id, status: 'DISMISSED' })}>Dismiss false positive</Button>
                    <Button size="small" onClick={() => reviewNote.mutate({ id: n.id, status: 'RESOLVED' })}>Resolved</Button>
                  </Box>
                )}
              </Paper>
            );
          })}
          {canUpdate && (
            <Box mt={2}>
              <Typography variant="caption" display="block" gutterBottom>Add human note</Typography>
              <TextField size="small" fullWidth label="Title" value={noteTitle} onChange={(e) => setNoteTitle(e.target.value)} sx={{ mb: 1 }} />
              <TextField size="small" fullWidth multiline minRows={2} label="Description" value={noteBody} onChange={(e) => setNoteBody(e.target.value)} sx={{ mb: 1 }} />
              <Button
                size="small"
                variant="contained"
                disabled={!noteTitle.trim() || createNote.isPending}
                onClick={() => createNote.mutate()}
              >
                Save Note
              </Button>
            </Box>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="qa-panel">
          <Typography variant="subtitle2" gutterBottom>QA Panel</Typography>
          {!canReadScore ? (
            <Alert severity="warning">QA scores require call_qa.scoring.read.</Alert>
          ) : scoreLoading ? (
            <CircularProgress size={20} />
          ) : !score ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Unscored. Run automated scoring after transcript completes, or score manually.
              </Typography>
              {canRunScore && transcriptionStatus === 'COMPLETED' && (
                <Box display="flex" gap={1}>
                  <Button size="small" variant="contained" disabled={requestScoring.isPending} onClick={() => requestScoring.mutate({ force: true, mode: 'AI' })}>
                    Run AI Score
                  </Button>
                  <Button size="small" variant="outlined" disabled={requestScoring.isPending} onClick={() => requestScoring.mutate({ force: true, mode: 'MANUAL' })}>
                    Manual Scorecard
                  </Button>
                </Box>
              )}
              {isSuperUser && (
                <Button size="small" variant="text" color="secondary" disabled={reanalyze.isPending} sx={{ mt: 1 }} onClick={() => reanalyze.mutate()}>
                  {reanalyze.isPending ? 'Re-analyzing...' : 'Re-analyze (super user)'}
                </Button>
              )}
            </Box>
          ) : (
            <Box>
              <Typography variant="h5" data-testid="qa-overall-score">
                {score.final_overall_score != null ? `${score.final_overall_score}%` : '—'}
              </Typography>
              <Chip
                size="small"
                label={score.final_overall_status || '—'}
                color={score.final_overall_status === 'FAIL' ? 'error' : score.final_overall_status === 'PASS' ? 'success' : 'warning'}
                sx={{ mr: 1 }}
              />
              <Chip size="small" label={score.scorecard_version_label} sx={{ mr: 1 }} />
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                Automated: {score.automated_overall_score ?? '—'} ({score.automated_overall_status || '—'}) · Review: {score.review_status}
              </Typography>
              {criticalCriteria.length > 0 && (
                <Alert severity="error" sx={{ mt: 1 }} data-testid="qa-critical-failures">
                  Critical failures: {criticalCriteria.map((c: any) => c.name).join(', ')}
                </Alert>
              )}
              <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 1, mb: 1 }}>
                {canRunScore && (
                  <Button size="small" variant="outlined" disabled={requestScoring.isPending} onClick={() => requestScoring.mutate({ force: true })}>
                    Re-score
                  </Button>
                )}
                {isSuperUser && (
                  <Button size="small" variant="outlined" color="secondary" disabled={reanalyze.isPending} onClick={() => reanalyze.mutate()}>
                    {reanalyze.isPending ? 'Re-analyzing...' : 'Re-analyze'}
                  </Button>
                )}
                {canOverrideScore && score.review_status === 'PENDING_REVIEW' && (
                  <Button size="small" variant="contained" disabled={acceptScore.isPending} onClick={() => acceptScore.mutate()}>
                    Accept AI Result
                  </Button>
                )}
                {canManageInvestigations && (
                  <Button
                    size="small"
                    variant="outlined"
                    data-testid="create-investigation-from-call"
                    disabled={createInvestigation.isPending}
                    onClick={() => createInvestigation.mutate()}
                  >
                    Create Investigation
                  </Button>
                )}
                {canManageCoaching && call.employee_id && (
                  <Button
                    size="small"
                    variant="outlined"
                    data-testid="create-coaching-from-call"
                    disabled={createCoaching.isPending}
                    onClick={() => createCoaching.mutate()}
                  >
                    Create Coaching
                  </Button>
                )}
              </Box>
              {scoreCriteria.map((c: any) => (
                <Box key={c.id} sx={{ mb: 1, p: 1, borderLeft: 2, borderColor: c.is_critical_failure ? 'warning.main' : 'divider', borderRadius: 1 }}>
                  <Typography variant="body2">
                    <strong>{c.section}</strong> — {c.name} ({c.weight})
                  </Typography>
                  {(() => {
                    const res = String(c.final_result || c.automated_result || '').toUpperCase();
                    const rc: 'success' | 'warning' | 'error' | 'default' = res === 'PASS' ? 'success'
                      : res === 'FAIL' ? 'error' : res === 'NOT_APPLICABLE' ? 'default' : 'warning';
                    return <Chip size="small" variant={rc === 'default' ? 'outlined' : 'filled'} color={rc} label={c.final_result || c.automated_result || '—'} sx={{ mr: 1 }} />;
                  })()}
                  {c.automated_confidence != null && (
                    <Typography variant="caption" component="span">conf {Math.round(c.automated_confidence * 100)}% · </Typography>
                  )}
                  <Typography variant="body2">{c.human_reason || c.automated_reason}</Typography>
                  {c.evidence_start_ms != null ? (
                    <Button
                      size="small"
                      data-testid={`qa-evidence-${c.criterion_id}`}
                      onClick={() => seekTo(c.evidence_start_ms, c.evidence_segment_id)}
                    >
                      Click to Hear {formatTimestamp(c.evidence_start_ms)}–{formatTimestamp(c.evidence_end_ms || c.evidence_start_ms)}
                    </Button>
                  ) : (
                    <Typography variant="caption" color="text.secondary">No matching evidence</Typography>
                  )}
                  {canOverrideScore && c.final_result !== 'PASS' && (
                    <Button size="small" sx={{ ml: 1 }} onClick={() => overrideScore.mutate(c.criterion_id)}>
                      Override → PASS
                    </Button>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }} data-testid="qa-flags-panel">
          <Typography variant="subtitle2" gutterBottom>Risk / Phrase Flags</Typography>
          <Alert severity="info" sx={{ mb: 1 }}>
            Phrase matches recommend review — they are not automatic violations.
          </Alert>
          {callFlags.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No phrase flags on this call.</Typography>
          ) : callFlags.map((f: any) => (
            <Box key={f.id} sx={{ mb: 1, p: 1, borderLeft: 3, borderColor: f.severity === 'critical' ? 'error.main' : 'warning.main' }}>
              <Chip size="small" label={f.category} sx={{ mr: 1 }} />
              <Chip size="small" label={f.speaker_role || 'ANY'} sx={{ mr: 1 }} />
              <Typography variant="body2" component="span">
                “{f.matched_phrase}” · {f.rule_name}
              </Typography>
              {f.start_ms != null && (
                <Button
                  size="small"
                  sx={{ ml: 1 }}
                  data-testid={`qa-flag-evidence-${f.id}`}
                  onClick={() => seekTo(f.start_ms, f.segment_id)}
                >
                  Click to Hear {formatTimestamp(f.start_ms)}
                </Button>
              )}
            </Box>
          ))}
        </Paper>
      </Box>

      {/* ---- Call Details tab ---- */}
      <Box hidden={activeTab !== 'details'}>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Call Information</Typography>
          <Typography variant="body2">Employee: {call.employee_name || '—'}</Typography>
          <Typography variant="body2">Computer: {call.device_name || call.device_id || '—'}</Typography>
          <Typography variant="body2">Campaign: {call.campaign_name || '—'}</Typography>
          <Typography variant="body2">Date/Time: {moment(call.started_at).format('MMM D, YYYY h:mm A')}</Typography>
          <Typography variant="body2">
            Duration: {formatCallDurationLabel(call.duration_seconds, measuredAudioSeconds)}
          </Typography>
          <Typography variant="body2">Classification: {classificationLabelMap[classification] || classification}</Typography>
          <Typography variant="body2">Recording: {call.recording_status || '—'}</Typography>
          <Typography variant="body2">Capture mode: {captureModeLabel[captureMode] || captureMode}</Typography>
          <Typography variant="body2">
            Detection: {call.detection_method ? String(call.detection_method).replace(/_/g, ' ') : '—'}
            {call.detection_confidence != null ? ` (${Math.round(Number(call.detection_confidence) * 100)}%)` : ''}
          </Typography>
          <Typography variant="body2">Phone: {call.customer_phone || '—'}</Typography>
          <Typography variant="body2">Account: {call.account_number || '—'}</Typography>
          <Typography variant="body2">Contract: {call.contract_number || '—'}</Typography>
          <Typography variant="body2">Provider: {call.provider || '—'} / {call.provider_call_id || '—'}</Typography>
          <Typography variant="body2">Language: {transcriptPayload?.transcript?.language || '—'}</Typography>
          <Typography variant="body2">
            Transcription: {providerBlocked ? 'PROVIDER NOT CONFIGURED' : transcriptionStatus}
          </Typography>
          <Typography variant="body2">Analysis: {analysisStatusLabel}</Typography>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Recording</Typography>
          {call.has_recording ? (
            canDownload ? (
              <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  data-testid="details-download-recording"
                  onClick={hasDualChannelAudio ? downloadCombinedStereo : downloadCallRecording}
                >
                  Download
                </Button>
                <Typography variant="caption" color="text.secondary">
                  {captureModeLabel[captureMode] || captureMode}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                You don’t have permission to download recordings.
              </Typography>
            )
          ) : (
            <Typography variant="body2" color="text.secondary">No recording is available for this call.</Typography>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Transcription</Typography>
          {(transcriptionStatus === 'PENDING' || transcriptionStatus === 'PROCESSING') && (
            <Alert severity="info" sx={{ mb: 1 }} icon={<CircularProgress size={16} />}>
              Transcription {transcriptionStatus.toLowerCase()}…
            </Alert>
          )}
          {transcriptionStatus === 'FAILED' && (
            <Alert severity="error" sx={{ mb: 1 }}>
              Transcription failed{transcriptError ? `: ${transcriptError}` : ''}.
            </Alert>
          )}
          {transcriptionStatus === 'BLOCKED' && (
            <Alert severity="warning" sx={{ mb: 1 }} data-testid="transcription-blocked-alert">
              Transcription blocked — AI provider not configured
              {transcriptError ? ` (${transcriptError})` : ''}. Recording is unaffected.
            </Alert>
          )}
          {transcriptionStatus === 'NO_SPEECH' && (
            <Alert severity="info" sx={{ mb: 1 }} data-testid="no-speech-alert">
              Transcription completed — no usable speech detected (this is not a failure).
            </Alert>
          )}
          {canUpdate && call.has_recording && (
            <Box display="flex" gap={1} flexWrap="wrap">
              {(transcriptionStatus === 'NOT_REQUESTED' || transcriptionStatus === 'FAILED' || transcriptionStatus === 'BLOCKED') && (
                <Button
                  size="small"
                  variant="contained"
                  disabled={requestTranscription.isPending}
                  onClick={() => requestTranscription.mutate(
                    transcriptionStatus === 'FAILED' || transcriptionStatus === 'BLOCKED',
                  )}
                >
                  {transcriptionStatus === 'FAILED' || transcriptionStatus === 'BLOCKED' ? 'Retry' : 'Request Transcription'}
                </Button>
              )}
              {(transcriptionStatus === 'COMPLETED' || transcriptionStatus === 'NO_SPEECH') && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={requestTranscription.isPending}
                  onClick={() => requestTranscription.mutate(true)}
                >
                  Re-transcribe
                </Button>
              )}
            </Box>
          )}
        </Paper>

        {canUpdate && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Link Employee / Campaign</Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Employee</InputLabel>
                <Select
                  label="Employee"
                  value={call.employee_id || ''}
                  onChange={(e) => linkEmp.mutate(e.target.value || null)}
                >
                  <MenuItem value="">Unlinked</MenuItem>
                  {employees.map((e: any) => (
                    <MenuItem key={e.id} value={e.id}>{e.firstName} {e.lastName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Campaign</InputLabel>
                <Select
                  label="Campaign"
                  value={call.campaign_id || ''}
                  onChange={(e) => linkCamp.mutate(e.target.value || null)}
                >
                  <MenuItem value="">None</MenuItem>
                  {campaigns.map((c: any) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

/** Seed Calls Day filter once: URL wins, else local today (YYYY-MM-DD). */
function initialCallQaDay(searchParams: URLSearchParams): string {
  const fromUrl = searchParams.get('day') || searchParams.get('date') || searchParams.get('date_from') || '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(fromUrl)) return fromUrl;
  return moment().format('YYYY-MM-DD');
}

const CallQaCallsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canRead = permissions.includes('call_qa.calls.read');
  const canImport = permissions.includes('call_qa.calls.import');
  const canReadTranscript = permissions.includes('call_qa.transcripts.read');

  // Seed once from landing URL (day|date|date_from) or local today — not a stale hardcoded date.
  const seededDayRef = useRef<string | null>(null);
  if (seededDayRef.current == null) {
    seededDayRef.current = initialCallQaDay(searchParams);
  }
  const seededDay = seededDayRef.current;

  const [day, setDay] = useState(seededDay);
  const [filters, setFilters] = useState({
    date_from: seededDay,
    date_to: seededDay,
    customer_name: '',
    customer_phone: '',
    account_number: '',
    employee_id: '',
    campaign_id: '',
    device_id: '',
    direction: '',
    recording_source: '',
    recording_status: '',
    provider: '',
    attention: '',
    disposition: '',
    disclosure_status: '',
    qa_status: '',
    page: 1,
  });
  const [transcriptQuery, setTranscriptQuery] = useState('');
  const [speakerRole, setSpeakerRole] = useState('');
  const [searchMode, setSearchMode] = useState<'calls' | 'transcript'>('calls');
  const [importOpen, setImportOpen] = useState(false);
  const [scope, setScope] = useState<'computer' | 'person'>('person');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);

  const { data: usersData } = useQuery({
    queryKey: ['usersForCallQaList'],
    queryFn: () => apiClient.users.getAll(),
    enabled: canRead,
  });
  const { data: campaignsData } = useQuery({
    queryKey: ['campaignsForCallQaList'],
    queryFn: () => apiClient.campaigns.getAll(),
    enabled: canRead,
  });

  const employees = useMemo(() => {
    const raw = usersData?.data?.data ?? usersData?.data;
    return Array.isArray(raw) ? raw : [];
  }, [usersData]);
  const campaigns = useMemo(() => {
    const raw = campaignsData?.data?.data ?? campaignsData?.data;
    return Array.isArray(raw) ? raw : [];
  }, [campaignsData]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['callQaCalls', filters],
    queryFn: () => apiClient.callQa.listCalls({
      ...filters,
      page: filters.page,
      page_size: 25,
    }),
    enabled: canRead && searchMode === 'calls',
  });

  const transcriptSearch = useQuery({
    queryKey: ['callQaTranscriptSearch', transcriptQuery, speakerRole, filters],
    queryFn: () => apiClient.callQa.searchTranscripts({
      q: transcriptQuery,
      speaker_role: speakerRole || undefined,
      date_from: filters.date_from || undefined,
      date_to: filters.date_to || undefined,
      customer_name: filters.customer_name || undefined,
      customer_phone: filters.customer_phone || undefined,
      account_number: filters.account_number || undefined,
      employee_id: filters.employee_id || undefined,
      campaign_id: filters.campaign_id || undefined,
      direction: filters.direction || undefined,
      provider: filters.provider || undefined,
      page: 1,
      page_size: 25,
    }),
    enabled: canReadTranscript && searchMode === 'transcript' && transcriptQuery.trim().length > 0,
  });

  const { data: devicesData } = useQuery({
    queryKey: ['computersForCallQaList'],
    queryFn: () => apiClient.monitoring.getDevices({}),
    enabled: canRead,
  });
  const devices = useMemo(() => {
    const raw = devicesData?.data?.data ?? devicesData?.data ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [devicesData]);

  const computerNamesByEmployeeId = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const device of devices) {
      const employeeId = device?.employee?.id ? String(device.employee.id) : '';
      const name = String(device?.device_name || device?.computer_name || '').trim();
      if (!employeeId || !name) continue;
      const names = map.get(employeeId) || [];
      if (!names.includes(name)) names.push(name);
      map.set(employeeId, names);
    }
    for (const names of Array.from(map.values())) {
      names.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }
    return map;
  }, [devices]);

  const payload = data?.data?.data;
  const rows = payload?.rows || [];
  const summary = payload?.summary || { total: payload?.total || 0, flagged: 0, serious: 0, warnings: 0, disclosure_missing: 0, not_analyzed: 0, no_speech: 0 };
  const searchRows = transcriptSearch.data?.data?.data?.rows || [];

  // Auto-select the first call so the review pane is populated (skip in tests to keep them list-only).
  useEffect(() => {
    if (process.env.NODE_ENV === 'test') return;
    if (searchMode !== 'calls') return;
    if (!selectedCallId && rows.length > 0) setSelectedCallId(rows[0].id);
    if (selectedCallId && rows.length > 0 && !rows.some((r: any) => r.id === selectedCallId)) {
      setSelectedCallId(rows[0].id);
    }
  }, [rows, searchMode, selectedCallId]);

  const onDayChange = (value: string) => {
    setDay(value);
    setFilters((f) => ({ ...f, date_from: value, date_to: value, page: 1 }));
  };

  /** Advanced Date From/To are what the API uses; keep Day label honest when they diverge. */
  const onAdvancedDateChange = (key: 'date_from' | 'date_to', value: string) => {
    setFilters((f) => {
      const next = { ...f, [key]: value, page: 1 };
      if (next.date_from && next.date_from === next.date_to) {
        setDay(next.date_from);
      }
      return next;
    });
  };

  if (!canRead) {
    return <Alert severity="warning">Calls require call_qa.calls.read.</Alert>;
  }

  return (
    <Box>
      {/* ===== Filter bar ===== */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.secondary', fontWeight: 700 }}>
          Showing
        </Typography>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select
            value={scope}
            onChange={(e) => {
              const next = e.target.value as 'computer' | 'person';
              setScope(next);
              // Clear the hidden dimension so a leftover employee_id/device_id cannot
              // zero out the list after switching "By computer" ↔ "By agent".
              setFilters((f) => ({
                ...f,
                page: 1,
                ...(next === 'computer' ? { employee_id: '' } : { device_id: '' }),
              }));
            }}
          >
            <MenuItem value="person">By agent</MenuItem>
            <MenuItem value="computer">By computer</MenuItem>
          </Select>
        </FormControl>
        {scope === 'computer' ? (
          <Box sx={{ minWidth: 220 }}>
            <SearchableSelect
              label="Computer"
              size="small"
              value={filters.device_id}
              onChange={(value) => setFilters((f) => ({ ...f, device_id: String(value ?? ''), page: 1 }))}
              includeAllOption
              allLabel="All computers"
              allValue=""
              placeholder="Search computer..."
              options={devices.map((d: any) => {
                const agentName = String(d.employee?.name || '').trim();
                return {
                  value: d.id || d.device_id,
                  label: String(d.device_name || d.computer_name || d.device_id || d.id || ''),
                  secondary: agentName || undefined,
                };
              })}
            />
          </Box>
        ) : (
          <Box sx={{ minWidth: 220 }}>
            <SearchableSelect
              label="Agent"
              size="small"
              value={filters.employee_id}
              onChange={(value) => setFilters((f) => ({ ...f, employee_id: String(value ?? ''), page: 1 }))}
              includeAllOption
              allLabel="All agents"
              allValue=""
              placeholder="Search agent..."
              options={employees.map((e: any) => {
                const computers = computerNamesByEmployeeId.get(String(e.id)) || [];
                return {
                  value: e.id,
                  label: e.employeeId
                    ? `${e.firstName} ${e.lastName} (${e.employeeId})`
                    : `${e.firstName} ${e.lastName}`,
                  secondary: computers.length ? computers.join(', ') : undefined,
                };
              })}
            />
          </Box>
        )}
        <TextField
          size="small"
          type="date"
          label="Day"
          InputLabelProps={{ shrink: true }}
          value={day}
          onChange={(e) => onDayChange(e.target.value)}
          helperText={
            filters.date_from && filters.date_to && filters.date_from !== filters.date_to
              ? `List uses ${filters.date_from} → ${filters.date_to}`
              : undefined
          }
          FormHelperTextProps={{ sx: { m: 0, whiteSpace: 'nowrap' } }}
        />
        {canReadTranscript && (
          <TextField
            size="small"
            sx={{ minWidth: 200, flex: 1 }}
            placeholder='Search transcripts — e.g. "promise to pay"'
            value={transcriptQuery}
            onChange={(e) => setTranscriptQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && transcriptQuery.trim()) setSearchMode('transcript');
            }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" onClick={() => (searchMode === 'calls' ? refetch() : transcriptSearch.refetch())}>
          Refresh
        </Button>
        <Button variant="outlined" onClick={() => setShowFilters((v) => !v)}>
          {showFilters ? 'Hide Filters' : 'Filters'}
        </Button>
        {searchMode === 'transcript' && (
          <Button variant="text" onClick={() => setSearchMode('calls')}>Show Call List</Button>
        )}
        {canImport && (
          <Button variant="contained" onClick={() => setImportOpen(true)}>Import Recording</Button>
        )}
      </Paper>

      {/* ===== Stat tiles ===== */}
      {searchMode === 'calls' && (
        <Box mb={2}>
          <Typography
            variant="overline"
            sx={{ display: 'block', color: 'text.secondary', letterSpacing: '.06em', lineHeight: 1.6 }}
          >
            List totals · all calls matching these filters
          </Typography>
          <Box display="flex" gap={1.5} flexWrap="wrap" data-testid="call-list-summary">
          <StatTile n={summary.total ?? payload?.total ?? 0} label="calls" testid="count-total" />
          <StatTile n={summary.flagged || 0} label="flagged" accent="amber" testid="count-flagged" />
          <StatTile n={summary.serious || 0} label="serious" accent="red" testid="count-serious" />
          <StatTile n={summary.warnings || 0} label="with warnings" accent="amber" testid="count-warnings" />
          <StatTile n={summary.not_analyzed || 0} label="not analyzed" testid="count-not-analyzed" />
          <StatTile n={summary.no_speech || 0} label="no speech" testid="count-no-speech" />
          </Box>
        </Box>
      )}

      {/* ===== Advanced filters (collapsible) ===== */}
      {showFilters && (
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {[
            ['date_from', 'Date From', 'date'],
            ['date_to', 'Date To', 'date'],
            ['customer_name', 'Customer Name', 'text'],
            ['customer_phone', 'Customer Phone', 'text'],
            ['account_number', 'Account #', 'text'],
            ['provider', 'Provider', 'text'],
          ].map(([key, label, type]) => (
            <Grid item xs={6} md={2} key={key}>
              <TextField
                size="small"
                fullWidth
                type={type}
                label={label}
                InputLabelProps={type === 'date' ? { shrink: true } : undefined}
                value={(filters as any)[key]}
                onChange={(e) => {
                  if (key === 'date_from' || key === 'date_to') {
                    onAdvancedDateChange(key, e.target.value);
                  } else {
                    setFilters((f) => ({ ...f, [key]: e.target.value, page: 1 }));
                  }
                }}
              />
            </Grid>
          ))}
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Direction</InputLabel>
              <Select
                label="Direction"
                value={filters.direction}
                onChange={(e) => setFilters((f) => ({ ...f, direction: e.target.value, page: 1 }))}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="INBOUND">Inbound</MenuItem>
                <MenuItem value="OUTBOUND">Outbound</MenuItem>
                <MenuItem value="UNKNOWN">Unknown</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Attention</InputLabel>
              <Select
                label="Attention"
                value={filters.attention}
                onChange={(e) => setFilters((f) => ({ ...f, attention: e.target.value, page: 1 }))}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="needs_review">Needs Review</MenuItem>
                <MenuItem value="has_notes">Has Notes</MenuItem>
                <MenuItem value="has_warning">Has Warning</MenuItem>
                <MenuItem value="serious">Serious/Critical</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
                <MenuItem value="disclosure_missing">Disclosure Missing</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Campaign</InputLabel>
              <Select
                label="Campaign"
                value={filters.campaign_id}
                onChange={(e) => setFilters((f) => ({ ...f, campaign_id: e.target.value, page: 1 }))}
              >
                <MenuItem value="">All</MenuItem>
                {campaigns.map((c: any) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Recording Status</InputLabel>
              <Select
                label="Recording Status"
                value={filters.recording_status}
                onChange={(e) => setFilters((f) => ({ ...f, recording_status: e.target.value, page: 1 }))}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="AVAILABLE">Available</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="FAILED">Failed</MenuItem>
                <MenuItem value="DELETED">Deleted</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      )}

      {searchMode === 'transcript' ? (
        transcriptSearch.isLoading ? (
          <CircularProgress size={24} />
        ) : (
          <Box>
            {searchRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No transcript matches.</Typography>
            ) : searchRows.map((row: any) => (
              <Paper key={row.call_id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                <Typography variant="subtitle2">
                  {row.employee_name || 'Unlinked'} · {row.customer_name || row.customer_phone || '—'} · {row.campaign_name || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  {moment(row.started_at).format('MMM D h:mm A')} · {row.match_count} match{row.match_count === 1 ? '' : 'es'}
                </Typography>
                {(row.matches || []).map((m: any) => (
                  <Box key={m.segment_id} sx={{ mb: 1 }}>
                    <Chip size="small" label={m.speaker_role} sx={{ mr: 1 }} color={speakerColor(m.speaker_role)} />
                    <Typography variant="body2" component="span">
                      {formatTimestamp(m.start_ms)} {m.excerpt}
                    </Typography>
                    <Button
                      size="small"
                      sx={{ ml: 1 }}
                      onClick={() => navigate(`/call-qa/calls/${row.call_id}?seek_ms=${m.seek_ms}`)}
                    >
                      Click to Hear {formatTimestamp(m.seek_ms)}
                    </Button>
                  </Box>
                ))}
              </Paper>
            ))}
          </Box>
        )
      ) : (
        /* ===== Master-detail ===== */
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ maxHeight: '72vh', overflow: 'auto' }}>
              {isLoading ? (
                <Box sx={{ p: 2 }}><CircularProgress size={24} /></Box>
              ) : rows.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No calls found.</Typography>
              ) : rows.map((row: any) => {
                const c = classificationChip(row.classification);
                const analyzed = String(row.analysis_status || '').toUpperCase() === 'COMPLETE';
                const rawAtt = row.attention_label;
                const attLabel = rawAtt && String(rawAtt).toLowerCase() !== 'clear'
                  ? rawAtt
                  : analyzed ? (rawAtt || 'Clear') : 'Not analyzed';
                const selected = selectedCallId === row.id;
                // One restrained attention signal: a left accent on calls that need review
                // (analyzed and not Clear). No red badge storm.
                const needsAttention = analyzed && !!attLabel && !/clear/i.test(String(attLabel));
                const accentColor = selected ? 'primary.main' : needsAttention ? 'warning.main' : 'transparent';
                return (
                  <Box
                    key={row.id}
                    onClick={() => setSelectedCallId(row.id)}
                    data-testid={`call-row-${row.id}`}
                    data-attention={needsAttention ? 'true' : 'false'}
                    sx={{
                      p: 1.25,
                      borderBottom: 1,
                      borderColor: 'divider',
                      cursor: 'pointer',
                      bgcolor: selected ? 'action.selected' : 'transparent',
                      borderLeft: 3,
                      borderLeftColor: accentColor,
                      '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {moment(row.started_at).format('MMM D, h:mm A')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatCallDurationLabel(row.duration_seconds, row.audio_duration_seconds)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" display="block" sx={{ fontSize: '0.85rem' }}>
                      {row.customer_name || row.customer_phone || '—'}
                      {row.employee_name ? ` · ${row.employee_name}` : ''}
                      {row.unlinked ? ' · Unlinked' : ''}
                    </Typography>
                    <Box display="flex" gap={0.5} alignItems="center" mt={0.75} flexWrap="wrap">
                      <Chip
                        size="small"
                        color={c.color}
                        variant={c.color === 'default' ? 'outlined' : 'filled'}
                        label={c.label}
                        data-testid={`classification-${row.id}`}
                      />
                      {needsAttention && (
                        <Chip size="small" variant="outlined" color="warning" label={attLabel} />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Paper>
            {searchMode === 'calls' && (
              <Box display="flex" gap={1} mt={1.5} alignItems="center">
                <Button
                  size="small"
                  disabled={filters.page <= 1}
                  onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
                >
                  Previous
                </Button>
                <Typography variant="caption">Page {payload?.page || 1} · {payload?.total || 0} total</Typography>
                <Button
                  size="small"
                  disabled={(payload?.page || 1) * (payload?.page_size || 25) >= (payload?.total || 0)}
                  onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
                >
                  Next
                </Button>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={8}>
            {selectedCallId ? (
              <CallQaCallDetailPage key={selectedCallId} callIdProp={selectedCallId} embedded />
            ) : (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Select a call on the left to review its recording, transcript, and QA.
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      )}

      <ImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        employees={Array.isArray(employees) ? employees : []}
        campaigns={Array.isArray(campaigns) ? campaigns : []}
      />
    </Box>
  );
};

export default CallQaCallsPage;
